import { INTL_DAY_MONTH_NAME_SHORT, INTL_SHORT_DATE_TIME_FORMAT, INTL_SHORT_DAY_NAME_DAY_MONTH, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isRewardFareClass, toBaseFareClass, type FareClassWithReward, type FlightsClassTypes } from 'constants/flight'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { CHARTER_CABIN_CONTENT, type CabinAmenity } from 'components/Flights/CharterFlights/charterCabinContent'
import { getCharterFareCabinLabel } from 'hooks/Flights/useCharterFlightLabels'
import type { CheapestAvailableReward } from 'hooks/OfferPage/useCheapestAvailableReward'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { min, nonNullable, sortByAll, uniqueBy } from 'lib/array/arrayUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { getOfferName, getOfferPropertyName, getPackageUniqueKey } from 'lib/offer/offerUtils'
import { getMaxNonInfantGuestCount } from 'lib/offer/occupancyUtils'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { extractPkgInclusions } from 'lib/offer/inclusionUtils'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import pluralize from 'lib/string/pluralize'
import { stripMarkdown } from 'lib/string/stripMarkdown'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { reportLog } from 'services/userMonitoring'
import invariant from 'tiny-invariant'
import { isBuyNowBookLaterAvailableForPackage } from 'components/OfferPage/BnblModal/utils'
import { shouldShowBookingsCountLabel, shouldShowRoomsRemainingLabel, shouldShowRoomsRemainingLabelForVariant } from 'components/Common/Labels/urgencyLabelVisibility'
import { getBedbankFreeCancellationDeadline, getFreeCancellationStartDate, shouldShowBedbankNonRefundableText } from 'lib/offer/cancellationPolicyUtils'
import { getFlashCancellationPolicyView } from 'components/Common/CancellationPolicies/Flash/getFlashCancellationPolicyView'
import { getPropertyCancellationLabelShape, getPropertyRefundableStatus, getRefundDescriptions } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import type { IntlShape } from 'react-intl'
import { getBedbankFlightBookingState, getBedbankPayAtPropertyVisibility } from 'components/OfferPage/Bedbank/payAtPropertyVisibility'
import { bedbankPlainDescription, getHotelRoomTypeName } from 'components/AccommodationKit/roomPackageUtils'
import { getOrderedPropertyInclusions, getPayAtPropertyDetails } from 'components/OfferPage/Property/singlePropertyUtils'
import { canShowCollapsedLeadPrice, getPropertyRateDisplayPrice } from 'components/OfferPage/Property/propertyCollapsedRoomPackageUtils'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'
import { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import { hasMapCoordinates } from 'components/OfferPage/NavigableOfferContent/mapCoordinates'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { getBedbankDisplayPricing } from 'lib/hotels/bedbankPricing'
import {
  formatAssistantRoomLabel,
  formatAssistantRoomOccupancyText,
  formatAssistantRoomsOccupancyText,
} from 'tripPlanner/assistant/utils/assistantRoomOccupancyText'
import { getAssistantOccupancyAgeThresholdsFromSnapshotSource } from 'tripPlanner/assistant/utils/assistantOccupancyAgeThresholds'
import { assistantSurfaceSnapshotChunkContentHash, buildAssistantSurfaceSnapshotChunkRef } from 'tripPlanner/assistant/surfaceSnapshots/fingerprint'
import type { AssistantSurfaceSnapshotBuildResult } from 'tripPlanner/assistant/surfaceSnapshots/useAssistantSurfaceSnapshot'
import type {
  AssistantSurfaceSnapshotChunk,
  AssistantSurfaceSnapshotChunkManifest,
  AssistantSurfaceSnapshotRequest,
  SurfaceAtomRequest,
  SurfaceCapabilityRequest,
  SurfaceManifestFactRequest,
  SurfaceRecentCommittedStateRequest,
  SurfaceTargetRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import type {
  FlashCharterItinerary,
  OfferPageAssistantSource,
  OfferPageSnapshotContribution,
  OfferPageSnapshotInput,
  OfferPageSnapshotPriceFormatContext,
  OfferPageSnapshotRoom,
  OfferPageSnapshotSelection,
} from './offerPageSnapshotTypes'
import {
  buildOfferPageSnapshotScopeKeyFromSource,
} from './offerPageSnapshotIdentity'
import {
  formatFacilityGroupsText,
  getPropertyFacilitiesSnapshotText,
  splitOfferPageSnapshotAtomText,
  toOfferPageSnapshotAtomText,
  toOfferPageSnapshotId,
  toOfferPageSnapshotLabel,
  toOfferPageSnapshotManifestFactText,
  toOfferPageSnapshotSourcePath,
  toOfferPageSnapshotTargetText,
} from './offerPageSnapshotText'
import { buildOfferPageFinePrintSectionsFromSources } from './offerPageFinePrintContext'
import { BEDBANK_THINGS_TO_KNOW_ROW_KEYS } from './offerPageThingsToKnowRowKeys'
import { buildDisplayedPriceSummary, displayContextField, formatEarnNumber, formatOfferPageLoyaltyEarnSummary, getOfferPageSnapshotPriceUnitLabel, nonEmptyString } from './offerPageContextFormatting'

const sortAtoms = (atoms: Array<SurfaceAtomRequest>) => sortByAll(atoms, [
  { selector: atom => atom.rankHint ?? 0, direction: 'asc' },
  { selector: atom => atom.sourcePath ?? '', direction: 'asc' },
  { selector: atom => atom.atomId ?? '', direction: 'asc' },
])

const sortTargets = (targets: Array<SurfaceTargetRequest>) => sortByAll(targets, [
  { selector: target => target.sourcePath ?? '', direction: 'asc' },
  { selector: target => target.targetId ?? '', direction: 'asc' },
])

const sortCommittedState = (committed: Array<SurfaceRecentCommittedStateRequest>) => sortByAll(committed, [
  { selector: state => state.expiresAt ?? '', direction: 'desc' },
  { selector: state => state.kind ?? '', direction: 'asc' },
  { selector: state => state.key ?? '', direction: 'asc' },
])

const sortManifestFacts = (facts: Array<SurfaceManifestFactRequest>) => sortByAll(facts, [
  { selector: fact => fact.key ?? '', direction: 'asc' },
  { selector: fact => fact.value ?? '', direction: 'asc' },
])

export type OfferPageSnapshotBuildResult = AssistantSurfaceSnapshotBuildResult

type OfferChunkId =
  | 'committed'
  | 'booking'
  | 'packages'
  | 'policies'
  | 'property'
  | 'reviews'
  | 'offer'

const capabilities: Array<SurfaceCapabilityRequest> = [{
  capability: 'surface_context.read',
  mode: 'available',
}, {
  // The offer-page focus executor consumes streamed focus_surface_content actions and scrolls to
  // registered targets. Auto-focusable targets are gated per-target by `modelUse`, so declaring
  // the capability only activates focus for targets that are both auto_focus_allowed and mounted.
  capability: 'surface_ui.focus_content',
  mode: 'available',
}]

function createFact(
  key: string,
  value: string | undefined,
  dataClassification: SurfaceManifestFactRequest['dataClassification'] = 'public_surface',
): SurfaceManifestFactRequest | undefined {
  const text = toOfferPageSnapshotManifestFactText(value)
  const factKey = toOfferPageSnapshotLabel(key)
  return text && factKey ? { key: factKey, value: text, dataClassification } : undefined
}

function createTarget(
  targetId: string,
  kind: string,
  label: string,
  sourcePath: string,
  options: {
    dataClassification?: SurfaceTargetRequest['dataClassification']
    linkStability?: SurfaceTargetRequest['linkStability']
    modelUse?: SurfaceTargetRequest['modelUse']
    parentTargetId?: string
    fallbackTargetId?: string
  } = {},
): SurfaceTargetRequest {
  const normalizedLabel = toOfferPageSnapshotLabel(label)
  const normalizedSourcePath = toOfferPageSnapshotSourcePath(sourcePath)
  const displayText = toOfferPageSnapshotTargetText(label) ?? normalizedLabel
  return {
    targetId,
    kind: toOfferPageSnapshotLabel(kind) ?? kind,
    label: normalizedLabel ?? label,
    displayText: displayText ?? normalizedLabel ?? label,
    sourcePath: normalizedSourcePath ?? sourcePath,
    dataClassification: options.dataClassification ?? 'public_surface',
    availability: 'rendered',
    modelUse: options.modelUse ?? 'inline_link_only',
    linkStability: options.linkStability ?? 'exact_fingerprint',
    ...(options.parentTargetId ? { parentTargetId: options.parentTargetId } : {}),
    ...(options.fallbackTargetId ? { fallbackTargetId: options.fallbackTargetId } : {}),
  }
}

function createAtom(
  atomId: string,
  kind: string,
  label: string,
  displayText: string | undefined,
  sourcePath: string,
  targetId: string,
  rankHint: number,
  dataClassification: SurfaceAtomRequest['dataClassification'] = 'public_surface',
  modelUse: SurfaceAtomRequest['modelUse'] = 'read_tool_only',
): SurfaceAtomRequest | undefined {
  const text = toOfferPageSnapshotAtomText(displayText)
  const normalizedLabel = toOfferPageSnapshotLabel(label)
  const normalizedSourcePath = toOfferPageSnapshotSourcePath(sourcePath)
  const normalizedKind = toOfferPageSnapshotLabel(kind)
  return text && normalizedLabel && normalizedSourcePath && normalizedKind ? {
    atomId,
    kind: normalizedKind,
    label: normalizedLabel,
    displayText: text,
    sourcePath: normalizedSourcePath,
    dataClassification,
    visibility: 'visible',
    modelUse,
    targetId,
    rankHint,
  } : undefined
}

function sourceDisplayName(source: OfferPageAssistantSource): string | undefined {
  switch (source.variant) {
    case 'flash':
      return nonEmptyString(getOfferPropertyName(source.offer)) ?? nonEmptyString(getOfferName(source.offer))
    case 'lme_property':
    case 'spp_property':
      return nonEmptyString(source.property.name)
    case 'bedbank':
      return nonEmptyString(source.offer.property.name) ?? nonEmptyString(getOfferName(source.offer))
    default:
      return assertUnreachable(source)
  }
}

function sourceLocation(source: OfferPageAssistantSource): string | undefined {
  switch (source.variant) {
    case 'flash':
      return source.offer.location
    case 'lme_property':
    case 'spp_property':
      return source.property.location.title ?? source.property.location.formattedAddress
    case 'bedbank':
      // `property.location` is a full formatted address, never rendered as-is -- the page shows
      // `locationTitleOverride` when set, else a short "city, state" summary.
      return source.offer.property.locationTitleOverride ??
        [source.offer.property.address.city, source.offer.property.address.stateProvinceName].filter(Boolean).join(', ')
    default:
      return assertUnreachable(source)
  }
}

function sourceRating(source: OfferPageAssistantSource): App.OfferRating | undefined {
  switch (source.variant) {
    case 'flash':
    case 'bedbank':
      return source.offer.property.rating
    case 'lme_property':
    case 'spp_property':
      return source.property.rating
    default:
      return assertUnreachable(source)
  }
}

function selectedDates(selection: OfferPageSnapshotSelection): string | undefined {
  const values = [selection.checkInDate, selection.checkOutDate].filter(Boolean)
  return values.length ? values.join(' to ') : undefined
}

function selectedGuests(
  selection: OfferPageSnapshotSelection,
  ageThresholds: ReturnType<typeof getAssistantOccupancyAgeThresholdsFromSnapshotSource>,
): string | undefined {
  return formatAssistantRoomsOccupancyText(selection.rooms, { ageThresholds })
}

// Matches `ReturnCharterFlightTile.tsx`'s own hardcoded `useMaldivesCharterBaggage` map exactly
// (that component's own comment: "Hardcoded baggage values for Maldives charter -- do not use API
// values") -- Business differs, every other cabin (including Economy) shares the same allowance.
const CHARTER_ITINERARY_BAGGAGE_TEXT: Record<FlightsClassTypes, string> = {
  economy: 'Carry-on 7kg; Checked bag 23kg',
  premium: 'Carry-on 7kg; Checked bag 23kg',
  lux_row: 'Carry-on 7kg; Checked bag 23kg',
  lux_row_pax_one: 'Carry-on 7kg; Checked bag 23kg',
  first: 'Carry-on 7kg; Checked bag 23kg',
  business: 'Carry-on 2 pieces up to 14kg, max 10kg per piece; Checked bag 2x up to 46kg total, max 32kg per piece',
}

// Looks up the real page's own city label (`ReturnCharterFlightTile.tsx` renders
// `originAirport.cityDesc`/`destinationAirport.cityDesc`, e.g. "Melbourne"/"Malé", never a raw
// IATA code) for whichever airport the leg actually starts/ends at -- deliberately keyed by the
// leg's own airport code rather than assuming the route's fixed origin, since a feeder-origin
// customer's leg can genuinely start at Sydney/Brisbane/Adelaide instead of Melbourne. Falls back
// to the raw code only if it's somehow outside this route's known airports.
function charterAirportCityName(charterBundleRoute: App.CharterBundleRoute, airportCode: string): string {
  const routeInfo = CHARTER_FLIGHT_DETAILS[charterBundleRoute]
  const knownAirports = [routeInfo.originAirport, routeInfo.destinationAirport, ...routeInfo.feederFlightOrigins]
  return knownAirports.find(airport => airport.airportCode === airportCode)?.cityDesc ?? airportCode
}

function charterItineraryLegText(leg: FlashCharterItinerary['departing'], charterBundleRoute: App.CharterBundleRoute): string {
  const locale = getCurrentIntl().locale
  const departure = intlDateFormatter(leg.departureDateTime, INTL_SHORT_DATE_TIME_FORMAT, locale)
  const stopsText = leg.isDirect ? 'direct' : 'with a stop'
  const fromCity = charterAirportCityName(charterBundleRoute, leg.departureAirportCode)
  const toCity = charterAirportCityName(charterBundleRoute, leg.arrivalAirportCode)
  return `${fromCity} to ${toCity}, departs ${departure}, ${stopsText}, ${leg.durationHours}h ${leg.durationMinutes}m`
}

/**
 * Matches `ReturnCharterFlightTile.tsx`'s own itinerary tile exactly -- city names and operator
 * name both come from `CHARTER_FLIGHT_DETAILS`, the same config the real tile reads from, rather
 * than duplicating them as literals here. "Airbus A330-200" stays hardcoded -- there's no aircraft
 * type field in that config either, and `MaldivesCharterFlightFareClassModal.tsx` hardcodes the
 * same subtitle on the real page (svc-flights doesn't return an aircraft type for charter routes).
 */
function charterItineraryText(
  itinerary: FlashCharterItinerary | undefined,
  // `OfferPageSnapshotSelection['fareClass']` (`FareClassWithReward`), not the narrower
  // `FlightsClassTypes` -- a reward fare class shares the same base cabin's baggage allowance, so
  // this converts before the lookup rather than requiring every call site to remember to.
  fareClass: OfferPageSnapshotSelection['fareClass'],
  // `undefined` only when `itinerary` is also undefined in practice (both only ever set together
  // for the Flash variant, see `FlashOfferPageSnapshotSource.charterItinerary`'s own doc comment) --
  // still guarded explicitly below rather than assumed, since nothing enforces that pairing at the
  // type level.
  charterBundleRoute: App.CharterBundleRoute | undefined,
): string | undefined {
  if (!itinerary || !charterBundleRoute) {
    return undefined
  }

  const departingText = charterItineraryLegText(itinerary.departing, charterBundleRoute)
  const returningText = charterItineraryLegText(itinerary.returning, charterBundleRoute)
  const operatorName = CHARTER_FLIGHT_DETAILS[charterBundleRoute].operatorName
  const baggage = CHARTER_ITINERARY_BAGGAGE_TEXT[toBaseFareClass(fareClass)]
  return `Departing: ${departingText}. Returning: ${returningText}. Operated by ${operatorName}, Airbus A330-200. Baggage for the selected fare: ${baggage}.`
}

/**
 * Static catalog only (names, inclusions, baggage) -- deliberately excludes live cash/reward
 * pricing for cabins other than whichever is currently selected. That data is only ever fetched by
 * the real "Change Class" modal once a customer opens it (a distinct fare-families/upsell +
 * rewards-availability request per cabin, not something any other part of the page triggers), so
 * pre-fetching it here would mean new network calls for cabins the customer may never ask about.
 * If asked for an exact price on another cabin, the assistant has no live data for it and should
 * say so rather than guess -- matching `getCharterFareCabinLabel`'s own real-page terminology
 * exactly so it can't describe a cabin by a name the customer wouldn't recognise.
 */
// `CHARTER_CABIN_CONTENT`'s messages are always plain literal strings in practice, but
// `MessageDescriptor.defaultMessage` is typed as `string | MessageFormatElement[]` (a pre-compiled
// ICU AST is possible in principle) -- narrows before joining rather than risking "[object Object]".
function cabinAmenityText(items: ReadonlyArray<CabinAmenity>): string {
  return items
    .map(item => (typeof item.message.defaultMessage === 'string' ? item.message.defaultMessage : undefined))
    .filter((text): text is string => !!text)
    .join('; ')
}

function charterFareClassesText(
  selectableFareClasses: ReadonlyArray<FareClassWithReward> | undefined,
): string | undefined {
  if (!selectableFareClasses?.length) {
    return undefined
  }

  const intl = getCurrentIntl()
  const baseCabins = uniqueBy(selectableFareClasses.map(toBaseFareClass), cabin => cabin)
  const rewardEligibleCabins = new Set(selectableFareClasses.filter(isRewardFareClass).map(toBaseFareClass))

  const cabinLines = baseCabins.map((cabin) => {
    const content = CHARTER_CABIN_CONTENT[cabin]
    const name = getCharterFareCabinLabel(intl, cabin)
    if (!content) {
      return name
    }

    const inclusions = cabinAmenityText(content.inclusions)
    const baggage = cabinAmenityText(content.baggage)
    const rewardNote = rewardEligibleCabins.has(cabin) ? ` ${getLoyaltyProgramDisplayName()} Rewards points redemption is available for this class.` : ''
    return `${name} -- ${inclusions}. Baggage: ${baggage}.${rewardNote}`
  })

  return `Selectable flight classes for this offer (live pricing only available for the currently selected class; open "Change Class" for exact prices on the others): ${cabinLines.join(' | ')}`
}

/**
 * Matches `FlashHotelRewardsAvailableCard`'s own suppression and copy exactly: only present when
 * the customer has enough Société points to actually afford at least one reward-eligible cabin (the
 * hook's own points-balance check), and never while already on a reward fare (see
 * `charterCheapestAvailableReward`'s own doc comment).
 */
function charterRewardAvailabilityText(
  reward: CheapestAvailableReward | undefined,
  priceFormatContext: OfferPageSnapshotPriceFormatContext,
): string | undefined {
  if (!reward) {
    return undefined
  }

  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const cabinName = getCharterFareCabinLabel(getCurrentIntl(), reward.fareClass)
  const pointsText = formatEarnNumber(reward.pointsPerPerson, priceFormatContext.regionCode)
  const cashText = formatCurrency(reward.cashPerPerson, priceFormatContext.currencyCode, priceFormatContext.regionCode, { format: 'roundedDollar' })
  return `${loyaltyProgramName} Rewards seat available for ${cabinName} class, from ${pointsText} points + ${cashText} cash per person.`
}

function selectionSnapshot(
  selection: OfferPageSnapshotSelection,
  ageThresholds: ReturnType<typeof getAssistantOccupancyAgeThresholdsFromSnapshotSource>,
  priceFormatContext: OfferPageSnapshotPriceFormatContext,
  // Only ever set for the Flash variant (`getEffectiveCharterBundleRoute`) -- burn (paying with
  // the customer's own points balance) is hard-disabled whenever a charter bundle is active
  // (`useOfferPageBurnPoints`/`useOfferPageBundledBurnPoints` both short-circuit to
  // `{ enabled: false }`), but the state reducer doesn't clear `pointsToApply` when the charter
  // toggle flips on -- so a stale non-zero value from a non-charter package on the same offer can
  // otherwise leak through and claim points are being applied when the real page shows zero.
  charterBundleRoute: App.CharterBundleRoute | undefined,
  // Only ever set for the Flash variant, and only once both a check-in and check-out date are
  // picked -- see `FlashOfferPageSnapshotSource.charterItinerary`'s own doc comment for why.
  charterItinerary: FlashCharterItinerary | undefined,
  // Only ever set for the Flash variant -- see `FlashOfferPageSnapshotSource.charterSelectableFareClasses`'s
  // own doc comment for why this is a static catalog only, no live cross-cabin pricing.
  charterSelectableFareClasses: ReadonlyArray<FareClassWithReward> | undefined,
  // Only ever set for the Flash variant -- see `FlashOfferPageSnapshotSource.charterCheapestAvailableReward`'s
  // own doc comment for its suppression rules.
  charterCheapestAvailableReward: CheapestAvailableReward | undefined,
): {
  atoms: Array<SurfaceAtomRequest>
  committed: Array<SurfaceRecentCommittedStateRequest>
  facts: Array<SurfaceManifestFactRequest>
  targets: Array<SurfaceTargetRequest>
} {
  // Same monotonic-counter approach as packageSnapshot's `rankHint` -- only orders this snapshot's
  // own atoms, doesn't need cross-target collision-avoidance, so hand-derived offsets add nothing.
  let nextRankHint = 0
  const rankHint = () => nextRankHint++
  const targets = [
    createTarget('booking:dates', 'booking_control', 'Dates', 'offer.booking.dates', {
      dataClassification: 'personalised_surface',
    }),
    createTarget('booking:guests', 'booking_control', 'Guests', 'offer.booking.guests', {
      dataClassification: 'personalised_surface',
    }),
  ]
  const dates = selectedDates(selection)
  const guests = selectedGuests(selection, ageThresholds)
  const multiRoomModeText = selection.rooms.length > 1
    ? (selection.multiRoomMode === 'individual' ? 'Booking each room individually' : 'Same room and rate for every room')
    : undefined
  // Burn is hard-disabled for charter bundles regardless of what `selection.pointsToApply` still
  // holds -- see the `charterBundleRoute` param doc-comment above.
  const pointsToApply = charterBundleRoute ? undefined : selection.pointsToApply
  const pointsPaymentText = pointsToApply
    ? `${formatEarnNumber(pointsToApply, priceFormatContext.regionCode)} ${getLoyaltyProgramDisplayName()} points applied to pay`
    : 'Not applying points to pay'
  const atoms = nonNullable([
    createAtom('booking:dates', 'booking_selection', 'Selected dates', dates, 'offer.booking.dates', 'booking:dates', rankHint(), 'personalised_surface'),
    createAtom('booking:guests', 'booking_selection', 'Selected guests', guests, 'offer.booking.guests', 'booking:guests', rankHint(), 'personalised_surface'),
    createAtom('booking:flight-mode', 'booking_selection', 'Flight selection', selection.flights ? 'Flights included' : 'Hotel only', 'offer.booking.flight-mode', 'booking:dates', rankHint(), 'personalised_surface'),
    // Only relevant when flights are actually part of the selection.
    createAtom('booking:airport', 'booking_selection', 'Departure airport', selection.flights ? selection.airportCode : undefined, 'offer.booking.airport', 'booking:dates', rankHint(), 'personalised_surface'),
    createAtom('booking:rooms:mode', 'booking_selection', 'Room booking mode', multiRoomModeText, 'offer.booking.rooms.mode', 'booking:guests', rankHint(), 'personalised_surface'),
    // `undefined` here means the points toggle is off OR not shown at all (not logged in, no
    // loyalty account, wrong region, below the minimum balance, or a charter bundle) -- those
    // cases are indistinguishable from this field alone, so all read as "not applying points"
    // rather than a specific reason.
    createAtom('booking:points-payment', 'booking_selection', 'Points payment', pointsPaymentText, 'offer.booking.points-payment', 'booking:dates', rankHint(), 'personalised_surface'),
  ])
  // `addTextAtom` (not `createAtom` above) -- unlike every other booking atom, these two can run
  // well past the platform's per-atom length limit (multiple cabins' full inclusions/baggage, or a
  // multi-leg itinerary description), so they need the same split-into-`:seg-N`-atoms handling
  // `addTextAtom` already gives package inclusions/fine print, not a single unbounded string.
  addTextAtom(atoms, 'booking:charter-itinerary', 'booking_selection', 'Charter flight itinerary', charterItineraryText(charterItinerary, selection.fareClass, charterBundleRoute), 'offer.booking.charter-itinerary', 'booking:dates', rankHint(), 'personalised_surface')
  addTextAtom(atoms, 'booking:charter-fare-classes', 'booking_selection', 'Available flight classes', charterFareClassesText(charterSelectableFareClasses), 'offer.booking.charter-fare-classes', 'booking:dates', rankHint(), 'commercial_surface')
  addTextAtom(atoms, 'booking:charter-reward-availability', 'booking_selection', 'Rewards seat availability', charterRewardAvailabilityText(charterCheapestAvailableReward, priceFormatContext), 'offer.booking.charter-reward-availability', 'booking:dates', rankHint(), 'commercial_surface')
  const facts = nonNullable([
    createFact('booking.duration_nights', selection.duration ? `${selection.duration}` : undefined, 'personalised_surface'),
    createFact('booking.price_mode', selection.priceMode, 'personalised_surface'),
    createFact('booking.fare_class', selection.fareClass, 'personalised_surface'),
  ])
  const committed: Array<SurfaceRecentCommittedStateRequest> = [{
    key: 'booking:dates',
    kind: 'booking_selection',
    label: 'Selected dates',
    displayText: dates ? toOfferPageSnapshotAtomText(dates) ?? dates : 'No dates selected',
    sourcePath: 'offer.booking.dates',
    dataClassification: 'personalised_surface',
    targetId: 'booking:dates',
  }]
  if (guests) {
    committed.push({
      key: 'booking:guests',
      kind: 'booking_selection',
      label: 'Selected guests',
      displayText: toOfferPageSnapshotAtomText(guests) ?? guests,
      sourcePath: 'offer.booking.guests',
      dataClassification: 'personalised_surface',
      targetId: 'booking:guests',
    })
  }
  // Flight toggle and points-payment always resolve to a value (their atoms above use the same
  // fallback text), so -- like dates/guests -- they're always reported here; it's the backend's
  // own turn-over-turn diff of this array that decides whether a value actually changed recently,
  // not this function. Applies across all hotel types since `selection.flights`/`pointsToApply`
  // are populated uniformly regardless of offer variant.
  committed.push({
    key: 'booking:flight-mode',
    kind: 'booking_selection',
    label: 'Flight selection',
    displayText: selection.flights ? 'Flights included' : 'Hotel only',
    sourcePath: 'offer.booking.flight-mode',
    dataClassification: 'personalised_surface',
    targetId: 'booking:dates',
  })
  committed.push({
    key: 'booking:points-payment',
    kind: 'booking_selection',
    label: 'Points payment',
    displayText: pointsPaymentText,
    sourcePath: 'offer.booking.points-payment',
    dataClassification: 'personalised_surface',
    targetId: 'booking:dates',
  })
  committed.push({
    key: 'booking:duration',
    kind: 'booking_selection',
    label: 'Selected duration',
    displayText: selection.duration ? `${selection.duration} night${selection.duration === 1 ? '' : 's'}` : 'No duration selected',
    sourcePath: 'offer.booking.duration',
    dataClassification: 'personalised_surface',
    targetId: 'booking:dates',
  })

  return { atoms, committed, facts, targets }
}

/**
 * Matches `FlashHotelContent.tsx`'s own `<LuxPlusNotificationBanner>` copy verbatim (real message
 * ids `mbgWcWDpuv2RDWPoUuYD` / `A5yA5AlpSHEL24rB4ypR`) rather than paraphrasing -- plain English,
 * not routed through `intl.formatMessage`, since this snapshot is English-only regardless of
 * locale (matching this file's own established convention for real-copy strings elsewhere).
 * Variant-agnostic on purpose: `luxPlusAccess`/`isLuxPlusEnabled` are pre-resolved identically by
 * every container (Flash/Bedbank/Property), so member-only coverage for a new offer type in the
 * future only needs that one container to populate these two fields -- nothing here has to change.
 */
function luxPlusAccessFact(source: OfferPageAssistantSource): SurfaceManifestFactRequest | undefined {
  if (!source.isLuxPlusEnabled) return undefined
  if (source.luxPlusAccess === 'memberOnly') {
    return createFact('offer.luxplus_access', 'You get exclusive access to this member-only offer')
  }
  if (source.luxPlusAccess === 'earlyAccess') {
    return createFact('offer.luxplus_access', 'Members get early access to this offer before the general public and more')
  }
  return undefined
}

function overviewSnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  facts: Array<SurfaceManifestFactRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const displayName = sourceDisplayName(source)
  const location = sourceLocation(source)
  const targets = [
    createTarget('page:overview', 'section', 'Overview', 'offer.overview', {
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }),
    createTarget('location:root', 'section', 'Location', 'offer.location', {
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }),
  ]
  const atoms = nonNullable([
    createAtom('page:overview:identity', 'property_identity', 'Property', displayName, 'offer.overview.identity', 'page:overview', 10),
    createAtom('location:root:summary', 'location', 'Location', location, 'offer.location.summary', 'location:root', 20),
    // Flash always renders `offer.name` (the deal title, distinct from the property name) as its
    // own visible line -- `getOfferName`'s fallback in `sourceDisplayName` never actually reaches
    // it for hotel-type offers (it resolves back to the property name instead), so it needs its
    // own atom.
    source.variant === 'flash'
      ? createAtom('page:overview:offer-name', 'offer_identity', 'Offer name', nonEmptyString(source.offer.name), 'offer.overview.offer-name', 'page:overview', 15)
      : undefined,
  ])
  const facts = nonNullable([
    createFact('offer.variant', source.variant),
    createFact('displayName', toOfferPageSnapshotLabel(displayName)),
    createFact('offer.display_name', displayName),
    createFact('offer.location', location),
    // Proactively visible (not read-tool-only) since this is the very first banner on the real
    // page, right below the sticky header (`FlashHotelContent.tsx`'s `<LuxPlusNotificationBanner>`,
    // gated identically: `luxPlusEnabled && !!offer.luxPlus?.access`) -- getting this wrong risks
    // telling a non-member customer they can freely book an offer they actually can't. Not Flash-
    // specific: the hidden-offer redirect that enforces member-only access (`getIsLuxPlusHiddenOffer`
    // in `OfferPage.tsx`) is checked before the variant even branches, so Bedbank/Property offers
    // can be just as member-restricted even though only Flash currently renders a persistent banner
    // once a member is past that gate.
    luxPlusAccessFact(source),
  ])

  return { atoms, facts, targets }
}

function addTextAtom(
  atoms: Array<SurfaceAtomRequest>,
  atomId: string,
  kind: string,
  label: string,
  displayText: string | undefined,
  sourcePath: string,
  targetId: string,
  rankHint: number,
  dataClassification: SurfaceAtomRequest['dataClassification'] = 'public_surface',
): void {
  const segments = splitOfferPageSnapshotAtomText(displayText)
  segments.forEach((segment, index) => {
    const segmentSuffix = segments.length > 1 ? `:seg-${index + 1}` : ''
    const atom = createAtom(
      `${atomId}${segmentSuffix}`,
      kind,
      label,
      segment,
      segments.length > 1 ? `${sourcePath}${segmentSuffix}` : sourcePath,
      targetId,
      rankHint + index,
      dataClassification,
    )
    if (atom) {
      atoms.push(atom)
    }
  })
}

/**
 * The real page shows this rating badge unconditionally near the property name, independent of
 * whether the (much heavier, lazily-mounted) review list further down the page has scrolled into
 * view yet -- so unlike the individual `review` atoms (still contributed separately by
 * `UserReviewSection`/`ReviewsModal` once mounted), this summary atom must not depend on that.
 * `rating` is already the same single, backend-resolved figure `isOfferRatingDisplayable` and
 * `OfferRating` use for that badge (LE vs. Google-sourced review counts are blended upstream in the
 * accommodation-content service, never distinguished here).
 *
 * The `reviews:root` target itself is always registered here, independent of whether the summary
 * atom is displayable -- the reviews section is a real place on the page regardless of the
 * aggregate-badge threshold, and `offerPageReviewsContribution.ts`'s individual review atoms
 * reference it as their `parentTargetId` fallback unconditionally too (a property can have real,
 * individually-visible reviews below the badge's minimum count). Without an always-present target,
 * those review atoms' parent reference could point at a target this snapshot never declared.
 */
function reviewsSummarySnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const targets = [
    // `offer.reviews.root`, not `offer.reviews` -- the reviews chunk filter matches on
    // `startsWith('offer.reviews.')`, so a sourcePath without that trailing segment would silently
    // fall out of the transmitted chunk entirely despite still appearing in the full atoms/targets
    // arrays that `buildOfferPageSnapshot`'s own unit tests check.
    createTarget('reviews:root', 'section', 'Reviews', 'offer.reviews.root', {
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }),
  ]
  const rating = sourceRating(source)
  if (!isOfferRatingDisplayable(rating)) {
    return { atoms: [], targets }
  }

  return {
    targets,
    atoms: nonNullable([
      createAtom(
        'reviews:root:summary',
        'review_summary',
        'Reviews',
        `${rating.score}/10 average from ${rating.reviewsTotal} ${rating.reviewsTotal === 1 ? 'review' : 'reviews'}`,
        'offer.reviews.summary',
        'reviews:root',
        0,
        'public_surface',
        'manifest',
      ),
    ]),
  }
}

function staticContentSnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const atoms: Array<SurfaceAtomRequest> = []
  const targets: Array<SurfaceTargetRequest> = []
  const addSectionTarget = (
    targetId: string,
    label: string,
    sourcePath: string,
    dataClassification: SurfaceTargetRequest['dataClassification'] = 'public_surface',
  ) => {
    targets.push(createTarget(targetId, 'section', label, sourcePath, {
      dataClassification,
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }))
  }
  // Splits an individual `page:overview` sub-block (description, additional description) into its
  // own focusable target -- otherwise every overview atom shares the single parent section target,
  // and the assistant can't focus just the description on its own.
  const addOverviewSubTarget = (targetId: string, kind: string, label: string, sourcePath: string) => {
    targets.push(createTarget(targetId, kind, label, sourcePath, {
      parentTargetId: 'page:overview',
      fallbackTargetId: 'page:overview',
      modelUse: 'auto_focus_allowed',
    }))
  }

  switch (source.variant) {
    case 'flash':
      addSectionTarget('page:highlights', 'Highlights', 'offer.property.highlights')
      addSectionTarget('facilities:root', 'Facilities', 'offer.property.facilities')
      addSectionTarget('fine-print:root', 'Things to know', 'offer.policies')
      addOverviewSubTarget('page:overview:description', 'description', 'About this offer', 'offer.overview.description')
      addTextAtom(atoms, 'page:overview:description:content', 'description', 'About this offer', source.offer.description, 'offer.overview.description', 'page:overview:description', 30)
      addTextAtom(atoms, 'page:highlights:content', 'highlights', 'Highlights', source.offer.highlights, 'offer.property.highlights', 'page:highlights', 40)
      addTextAtom(atoms, 'page:highlights:what-we-like', 'highlights', 'What we like', source.offer.whatWeLike, 'offer.property.what-we-like', 'page:highlights', 50)
      // Prefers structuredFacilities over the legacy `facilities` string when present -- an
      // experiment gate on which one renders is a client display concern, not a signal of
      // whether the content exists on the page.
      addTextAtom(atoms, 'facilities:root:content', 'facilities', 'Facilities', source.offer.structuredFacilities?.length
        ? formatFacilityGroupsText(source.offer.structuredFacilities.map(group => ({ name: group.group.name, itemNames: group.facilities.map(facility => facility.name) })))
        : source.offer.facilities, 'offer.property.facilities', 'facilities:root', 60)
      // When the property has map coordinates, the real page shows a location map instead of this
      // text -- matches `packageHasPropertyWithCoords`'s gate.
      addTextAtom(atoms, 'location:root:getting-there', 'location', 'Getting there', hasMapCoordinates(source.offer.property) ? undefined : source.offer.gettingThere, 'offer.location.getting-there', 'location:root', 70)
      break
    case 'bedbank':
      addSectionTarget('facilities:root', 'Facilities', 'offer.property.facilities')
      addSectionTarget('fine-print:root', 'Things to know', 'offer.policies')
      addOverviewSubTarget('page:overview:description', 'description', 'About this offer', 'offer.overview.description')
      addTextAtom(atoms, 'page:overview:description:content', 'description', 'About this offer', source.offer.description, 'offer.overview.description', 'page:overview:description', 30)
      addTextAtom(atoms, 'page:overview:tagline', 'tagline', 'Offer details', source.offer.tagline, 'offer.overview.tagline', 'page:overview', 40)
      // Only rendered when the offer actually has active promotions.
      addTextAtom(atoms, 'page:overview:promotion', 'promotion', 'Promotion details', source.offer.promotions.length ? source.offer.additionalPromoContent : undefined, 'offer.overview.promotion', 'page:overview', 50, 'commercial_surface')
      // `offer.amenities` is a legacy summary string used by offer list/card tiles, not the
      // rendered Facilities accordion -- read `facilityGroups`/`property.facilitiesOverride`
      // instead, since those are the fields the page actually shows.
      addTextAtom(atoms, 'facilities:root:amenities', 'facilities', 'Facilities', source.offer.property?.facilitiesOverride
      ?? formatFacilityGroupsText(source.offer.facilityGroups.map(group => ({ name: group.name, itemNames: group.values.map(value => value.name) }))), 'offer.property.facilities', 'facilities:root', 60)
      // Real page only renders this block at all when `offer.attractions` is truthy -- the
      // override never displays on its own, so gate on the base value before applying it.
      addTextAtom(atoms, 'location:root:attractions', 'location', 'Nearby attractions', source.offer.attractions ? (source.offer.property.attractionsOverride ?? source.offer.attractions) : undefined, 'offer.location.attractions', 'location:root', 70)
      break
    case 'lme_property':
    case 'spp_property':
      addSectionTarget('facilities:root', 'Facilities', 'offer.property.facilities')
      addSectionTarget('fine-print:root', 'Things to know', 'offer.policies')
      addTextAtom(atoms, 'page:overview:headline', 'headline', 'Property headline', source.property.headline, 'offer.overview.headline', 'page:overview', 30)
      addOverviewSubTarget('page:overview:description', 'description', 'About this property', 'offer.overview.description')
      addTextAtom(atoms, 'page:overview:description:content', 'description', 'About this property', source.property.description, 'offer.overview.description', 'page:overview:description', 40)
      addOverviewSubTarget('page:overview:additional-description', 'description', 'More about this property', 'offer.overview.additional-description')
      addTextAtom(atoms, 'page:overview:additional-description:content', 'description', 'More about this property', source.property.additionalDescription, 'offer.overview.additional-description', 'page:overview:additional-description', 50)
      // Single-unit (villa/rental) properties show a whole-property Sleeps/bedrooms/bathrooms
      // figure instead of the per-room one -- see the `hideCounts` gate on the per-room capacity
      // atom in packageSnapshot below.
      addTextAtom(atoms, 'page:overview:capacity', 'capacity', 'Capacity', singleUnitCapacityText(source.property.singleUnit), 'offer.overview.capacity', 'page:overview', 55)
      // PropertyNavigableOfferContent uses `structuredFacilities` for the facilities rework UI and
      // otherwise falls back to legacy `property.facilities` markdown, then amenity groups.
      addTextAtom(atoms, 'facilities:root:content', 'facilities', 'Facilities', getPropertyFacilitiesSnapshotText(source.property), 'offer.property.facilities', 'facilities:root', 60)
      addTextAtom(atoms, 'location:root:description', 'location', 'Location details', source.property.locationDescription, 'offer.location.description', 'location:root', 60)
      break
    default:
      assertUnreachable(source)
  }

  const referencedTargetIds = new Set([
    ...atoms.map(atom => atom.targetId),
    ...atoms.map(atom => atom.parentTargetId),
  ])

  return {
    atoms,
    targets: targets.filter(target => target.targetId && referencedTargetIds.has(target.targetId)),
  }
}

/**
 * Sanitises `getId(item)` once per item and drops items whose id sanitises to
 * empty, so callers never need a second `toOfferPageSnapshotId` call (and
 * never need an `as string` cast) to recover the id `uniqueBy` already saw.
 *
 * `packageSnapshot`'s `uniqueBy(withSnapshotId(items, getId), pair => pair.id)`
 * calls below have no collision-disambiguation, unlike the fine-print title
 * collisions handled in `offerPageFinePrintContext.ts` — deliberately, not an
 * oversight: package/room/rate ids are structured inventory identifiers, not
 * free text, so two distinct items sanitising to the same id would indicate a
 * genuine upstream data problem rather than an expected collision to absorb.
 */
function withSnapshotId<T>(items: Array<T>, getId: (item: T) => string | undefined): Array<{ item: T; id: string }> {
  return items.flatMap(item => {
    const id = toOfferPageSnapshotId(getId(item))
    return id ? [{ item, id }] : []
  })
}

/**
 * Resolves which already-built package/rate target a multi-room booking's
 * room slot maps to, so its selection atom can link to real inventory
 * instead of duplicating that inventory's own content. Returns undefined
 * (dropping the room's atom entirely) rather than a dangling reference when
 * the room slot's roomId/rateId don't resolve to anything actually built —
 * e.g. no room/rate chosen yet, or stale ids from a data inconsistency.
 */
function resolveRoomSlotTargetId(
  source: OfferPageAssistantSource,
  room: OfferPageSnapshotRoom,
  targetIds: Set<string>,
): string | undefined {
  if (source.variant === 'flash') {
    if (!room.roomId || !room.rateId) {
      return undefined
    }

    const uniqueKey = getPackageUniqueKey(room.roomId, source.selection.duration, room.rateId)
    const packageView = source.packageViews.find(view => view.package.uniqueKey === uniqueKey)
    const targetId = packageView ? `package:${toOfferPageSnapshotId(packageView.package.uniqueKey)}` : undefined
    return targetId && targetIds.has(targetId) ? targetId : undefined
  }

  const roomId = toOfferPageSnapshotId(room.roomId)
  if (!roomId) {
    return undefined
  }

  const rateId = toOfferPageSnapshotId(room.rateId)
  const rateTargetId = rateId ? `room-package:${roomId}:${rateId}` : undefined
  if (rateTargetId && targetIds.has(rateTargetId)) {
    return rateTargetId
  }

  const roomTargetId = `package:${roomId}`
  return targetIds.has(roomTargetId) ? roomTargetId : undefined
}

/**
 * Shared "Sleeps N · 2 bedrooms · 1 King Bed · 42m²" composer for the capacity atom across all 3
 * variants. `size` is expected pre-formatted (Property's `Room.size` already is; Flash's numeric
 * `sizeSqm` is converted to the same `"Xm²"` shape at the call site) so this stays a plain string
 * composer, not a unit formatter.
 */
function capacityText(params: {
  capacities: Array<App.RoomCapacity> | undefined
  bedDescription: string | undefined
  size: string | undefined
  bedrooms: number | undefined
}): string | undefined {
  const parts: Array<string> = []
  const sleeps = params.capacities?.length ? getMaxNonInfantGuestCount(params.capacities) : undefined
  if (sleeps) {
    parts.push(`Sleeps ${sleeps}`)
  }
  if (params.bedrooms) {
    parts.push(`${params.bedrooms} ${pluralize({ singular: 'bedroom', count: params.bedrooms }).text}`)
  }
  if (params.bedDescription) {
    parts.push(params.bedDescription)
  }
  if (params.size) {
    parts.push(params.size)
  }
  return parts.length ? parts.join(' · ') : undefined
}

/**
 * Single-unit (villa/rental) properties show a whole-property Sleeps/bedrooms/bathrooms figure
 * instead of per-room capacity -- see the `hideCounts` gate this mirrors on the per-room atom.
 * Matches `PropertyPropertyDetails`'s `SingleUnitHighlights` block exactly: all 4 rows
 * (including "Entire home" and a zero count) render unconditionally whenever `singleUnit` exists,
 * with no per-field truthy guard.
 */
function singleUnitCapacityText(singleUnit: NonNullable<App.Property.Property['singleUnit']> | undefined): string | undefined {
  if (!singleUnit) {
    return undefined
  }

  return [
    'Entire home',
    `Sleeps ${singleUnit.maxNonInfantOccupants}`,
    `${singleUnit.bedrooms} ${pluralize({ singular: 'bedroom', count: singleUnit.bedrooms }).text}`,
    `${singleUnit.bathrooms} ${pluralize({ singular: 'bathroom', count: singleUnit.bathrooms }).text}`,
  ].join(' · ')
}

/**
 * Mirrors the real tile's SOLD OUT / rooms-remaining / recently-booked urgency labels using
 * fields already on `packageView` -- a labelling exercise, not new data. `sold out` is a per-rate
 * state (unchanged from before), but the urgency labels themselves are room-type-level on the real
 * page -- see `includeUrgencyLabels`.
 *
 * Deliberately does not replicate `displayedRoomsBeforeSurcharge` (`useUrgentOfferRoomDateData`/
 * `getDynamicPricingUrgencyLabelData`'s live-adjusted-availability suppression, brand-gated and
 * backed by its own cache-triggered fetch) or the `croPopularOptionBookingRemaining` experiment's
 * alternate "Guest favourite" copy -- both would require re-deriving non-trivial, cache-dependent
 * logic this builder has no way to verify live; flagged as a follow-up rather than guessed at.
 */
function flashAvailabilityText(
  packageView: App.OfferPackageOptionView,
  params: {
    checkIn: string | undefined
    displayedAvailableRooms: number | undefined
    includeUrgencyLabels: boolean
  },
): string | undefined {
  if (!packageView.hotelAvailable) {
    return 'Sold out'
  }

  if (!params.includeUrgencyLabels || !params.checkIn) {
    return undefined
  }

  const parts: Array<string> = []
  const availableRooms = params.displayedAvailableRooms
  if (availableRooms && shouldShowRoomsRemainingLabel(availableRooms)) {
    parts.push(`${availableRooms} ${pluralize({ singular: 'room', count: availableRooms }).text} left`)
  }
  const booked = packageView.package.roomType.stats.booked
  if (booked && shouldShowBookingsCountLabel(booked, 'room')) {
    // English copy matches `BookingsCountLabel`'s real message id `GTtJEPbhGd04RM6BiFoi`
    // ("Booked {bookingCount} times in 3 days") -- kept as a plain string rather than routing
    // through `intl.formatMessage` since this snapshot is English-only regardless of locale.
    parts.push(`Booked ${booked} times in 3 days`)
  }
  return parts.length ? parts.join('; ') : undefined
}

/**
 * Mirrors the real tile's sold-out / exceeded-capacity states using fields already on
 * `App.BedbankOfferPackageView` -- a labelling exercise, not new data. Uses the literal
 * "Sold out" wording `BedbankOfferHeader.tsx`'s real copy uses ("Sold out on your dates"),
 * rather than a paraphrase, since that's the term a reader (model or human) will actually look
 * for when asking which rooms are unavailable.
 */
function bedbankAvailabilityText(packageView: App.BedbankOfferPackageView): string | undefined {
  if (!packageView.available) {
    return 'Sold out on your dates'
  }
  if (!packageView.capacityValid) {
    return 'Does not fit the selected number of guests'
  }
  return undefined
}

/**
 * Mirrors the per-room availability states `AccommodationRoomTileActions` renders (sold out /
 * exceeded capacity / hidden by filters) using fields already on `RoomRateViews` -- a labelling
 * exercise, not new data. Uses the literal "Sold out" wording `AccommodationRoomTileMessage.tsx`'s
 * real copy uses for this state ("This room is sold out on your dates..."), rather than a
 * paraphrase, since that's the term a reader (model or human) will actually look for when asking
 * which rooms are unavailable.
 */
function propertyRoomAvailabilityText(roomRateView: RoomRateViews): string | undefined {
  if (!roomRateView.datesAvailable) {
    return 'Sold out on your dates'
  }
  if (!roomRateView.roomFitsCapacity) {
    return 'Does not fit the selected number of guests'
  }
  if (roomRateView.allRatesQuickFiltered) {
    return 'Hidden by the currently applied filters'
  }
  return undefined
}

/**
 * Mirrors `PropertyRoomOption`'s `canShowRatePriceRoomsRemainingLabel` gate and
 * `RoomsRemainingLabel`'s own "rate-price" copy variant exactly, rather than a separate mirror of
 * either. Composed into the existing room-level availability atom via `??` (same pattern
 * `flashAvailabilityText` already uses) -- not a second, competing atom for the same target.
 */
function propertyRoomUrgencyText(
  roomRateView: RoomRateViews,
  hasCheckIn: boolean,
  isSppRoomUrgencyEnabled: boolean,
  priceFormatContext: { currencyCode: string; regionCode: string },
): string | undefined {
  const firstRate = roomRateView.rates[0]
  if (!isSppRoomUrgencyEnabled || !hasCheckIn || !firstRate) {
    return undefined
  }

  const availableRooms = roomRateView.availableRooms
  const canShow = shouldShowRoomsRemainingLabelForVariant({
    roomCount: typeof availableRooms === 'number' ? availableRooms : 0,
    hasRatePrice: true,
    threshold: 'rate',
    copyVariant: 'rate-price',
  })
  if (!canShow) {
    return undefined
  }

  const ratePrice = formatCurrency(getPropertyRateDisplayPrice(firstRate), priceFormatContext.currencyCode, priceFormatContext.regionCode, { format: 'roundedDollar' })
  return `Limited rooms left at ${ratePrice}`
}

/**
 * Calls the same `isBuyNowBookLaterAvailableForPackage` the real Flash CTA gate uses, rather than
 * a separate mirror of its formula. Does not exclude wedding-gift-registry bookings the way the
 * rendered gate additionally does -- there is no signal for that mode anywhere in the snapshot
 * source, so it can't be replicated here. "Buy now, choose dates later" is the fixed
 * customer-facing copy; do not invent a "pay later" variant.
 */
function flashDepositText(offer: App.HotelOffer, packageView: App.OfferPackageOptionView): string | undefined {
  const pkg = packageView.package
  if (isBuyNowBookLaterAvailableForPackage({
    capacityValid: packageView.capacityValid,
    hasBuyNowBookLater: offer.hasBuyNowBookLater,
    allowBuyNowBookLater: pkg.allowBuyNowBookLater,
  })) {
    return 'Buy now, choose dates later'
  }
  return undefined
}

/**
 * Calls the same `getBedbankFlightBookingState` the real `BedbankPackageTilePricing` tile uses for
 * both flight-bundle-toggle gates: pay-at-property is hidden while a flight-bundled booking is
 * active (`flightsBookingActive`), and pay-later is hidden whenever flights are enabled and
 * selected at all (`hideHotelPriceBlock`), regardless of a specific flight origin.
 */
function bedbankDepositText(
  rate: App.BedbankRate,
  params: {
    isPropertyCollectEnabled: boolean
    flightsEnabled: boolean
    isFlightsSelected: boolean
    hasFlightOrigin: boolean
  },
): string | undefined {
  const { flightsBookingActive, hideHotelPriceBlock } = getBedbankFlightBookingState(params)
  const { canPayAtProperty } = getBedbankPayAtPropertyVisibility({
    isPropertyCollectEnabled: params.isPropertyCollectEnabled,
    flightsBookingActive,
    rate,
  })
  if (canPayAtProperty) {
    return 'Pay at property'
  }
  if (!hideHotelPriceBlock && rate.paymentScheduleInfo?.staticSchedule?.supplierPaymentNumberOfDays) {
    return 'Pay later'
  }
  return undefined
}

function propertyDepositText(rate: App.EnrichedPropertyRate): string | undefined {
  if (getPayAtPropertyDetails(rate).shouldShowPill) {
    return 'Pay at property'
  }
  if (rate.paymentScheduleInfo?.staticSchedule?.supplierPaymentNumberOfDays) {
    return 'Pay later'
  }
  return undefined
}

// Static copy matching `FlexibleCancellationCharterFlightFees.tsx`'s own hardcoded fee amounts
// exactly (always AUD, regardless of the customer's own currency, same as that component). Only
// relevant once a charter bundle's flexible-cancellation fee-bearing window has actually started
// (`policyView.flexibleTimelineContext` -- the same gate that modal renders under), matching the
// exact period the real page shows these figures rather than during the free-cancellation window,
// when no fee applies at all.
const CHARTER_FLIGHT_CANCELLATION_FEES_TEXT = 'Charter flight fees apply on top of the hotel policy above: Economy A$200 change fee / A$995 cancellation fee per person; Business A$500 change fee / A$2,995 cancellation fee per person.'

/**
 * Uses `getFlashCancellationPolicyView` — the same decision helper as `FlashCancellationPolicies`
 * — so Trip Planner assistant copy cannot drift from the rendered offer-page gates.
 * `isChangePackage` is always false for offer-page package cards.
 */
function flashCancellationText(
  pkg: App.HotelPackage,
  checkInDate: string | undefined,
  charterDepartDate: string | undefined,
  propertyTimezoneOffset: number | undefined,
  isCharterModeEnabled: boolean,
  charterBundleRoute: App.CharterBundleRoute | undefined,
): string | undefined {
  const policy = pkg.roomRate.cancellationPolicy
  const policyView = getFlashCancellationPolicyView({
    cancellationPolicy: policy,
    propertyTimezoneOffset,
    checkInDate,
    charterDepartDate,
    isCharterModeEnabled,
    availableForPurchase: true,
  })

  if (policyView.showFreeCancellation) {
    // Matches `OfferPropertyCancellationPolicies`'s own two branches exactly: a flat 7-day-after-
    // purchase window before a check-in-derived deadline exists, or the computed deadline itself
    // once one does.
    const startDate = getFreeCancellationStartDate(
      policyView.policyReferenceDate,
      undefined,
      propertyTimezoneOffset,
    )
    const locale = getCurrentIntl().locale
    // Format via calendar YYYY-MM-DD so property utcOffset moments keep the same wall date as
    // moment `.format('D MMM')` (Intl on the raw Instant can shift a day on UTC hosts).
    return startDate
      ? `Free cancellation until ${intlDateFormatter(startDate.format(ISO_DATE_FORMAT), INTL_DAY_MONTH_NAME_SHORT, locale)} (Hotel only)`
      : 'Free cancellation within 7 days of purchase (Hotel only)'
  }

  if (policyView.flexibleTimelineContext) {
    // Matches `OfferPropertyFlexibleCancellationPoliciesView`'s own 3-way branch exactly.
    const { details } = policyView.flexibleTimelineContext
    const locale = getCurrentIntl().locale
    const flexibleText = !details.checkInDate
      ? 'Flexible cancellation up to 7 days after purchase (Hotel only)'
      : (details.startBasedOnCheckIn
          ? `Flexible cancellation until ${intlDateFormatter(details.startBasedOnCheckIn.format(ISO_DATE_FORMAT), INTL_SHORT_DAY_NAME_DAY_MONTH, locale)} (Hotel only)`
          : 'Flexible cancellation')
    return charterBundleRoute ? `${flexibleText} ${CHARTER_FLIGHT_CANCELLATION_FEES_TEXT}` : flexibleText
  }

  // Eligible-but-hidden flexible (charter resolving / expired window) → blank, same as the page.
  if (policyView.showNonRefundable) {
    return 'Non-refundable'
  }

  return undefined
}

/**
 * Uses the boolean refundable/partiallyRefundable flags for the primary visible label, not
 * `cancellationPolicy.description` -- that field is modal-only free text, not what's shown inline.
 * The free-cancellation deadline date and "(hotel only)" qualifier mirror
 * `BedbankPropertyCancellationPoliciesView` rendering with `showHotelOnly` set, which is
 * what every package-tile call site on the offer page actually uses.
 *
 * `showNonCancellation` mirrors `BedbankMultiSameRoomRoomOption`'s own call-site precedence: always
 * `false` once a promotion applies (the promotion's own cancellation text takes over below), and
 * otherwise `true` unless the `croFlexCfmrBedbankPackages` experiment is showing this rate a CFMR
 * insurance upsell instead. That experiment's eligibility is computed per room from that room's own
 * non-refundable rate set (`useOfferPageBedbankRates`), which isn't available to thread into this
 * snapshot without a hook call per room -- so this defaults to the pre-experiment `true` behaviour.
 * KNOWN GAP: for any non-refundable rate once check-in/check-out dates are picked, if this
 * experiment is actively allocating traffic to a variant, this can say "Non-refundable" for users
 * who are actually seeing insurance-upsell copy instead -- how large a share of views that affects
 * depends on the experiment's current Optimizely traffic split, which isn't visible from this repo.
 * KNOWN GAP: `sppBedbankRestrictiveCancellationPolicy` is a hook-gated experiment, so an eligible
 * rate reads "Free cancellation" here and "Flexible cancellation" on the page for variant users.
 */
function bedbankCancellationText(rate: App.BedbankRate, promotion: App.BedbankPromotion | undefined, hasCheckInCheckOutDates: boolean): string | undefined {
  const rateText = (() => {
    if (rate.refundable) {
      const date = getBedbankFreeCancellationDeadline(rate.cancellationPolicy)
      return date ? `Free cancellation until ${date} (hotel only)` : 'Free cancellation (hotel only)'
    }
    if (rate.partiallyRefundable) {
      return 'Partially refundable'
    }
    return shouldShowBedbankNonRefundableText(rate, !promotion) ? 'Non-refundable' : undefined
  })()

  // Matches `BedbankPromotionDetails`: the promotion's own cancellation-policy title only shows
  // before check-in/check-out dates are selected, and is a plain code comparison, not the
  // `getPromotionCancellationPolicyByCode` lookup -- that call is only used for the modal's longer
  // description, not this visible title.
  const promotionText = !hasCheckInCheckOutDates && promotion?.cancellationPolicy
    ? (promotion.cancellationPolicy === 'non-refundable' ? 'Non-refundable' : 'Free Cancellation')
    : undefined

  // Labelled, not a bare `'; '` join -- `rateText` and `promotionText` are two independent facts
  // (the real page renders them as two separate elements, not one sentence) and can disagree, e.g.
  // a refundable rate under a promotion whose own policy is "non-refundable". An unlabelled join
  // would read as self-contradictory rather than as "two different things".
  return [rateText, promotionText ? `Promotion: ${promotionText}` : undefined].filter(Boolean).join('; ') || undefined
}

/**
 * Calls the same `getPropertyRefundableStatus`/`getPropertyCancellationLabelShape` the real
 * `PropertyCancellationPolicy` component uses, rather than a separate mirror of their branch
 * logic -- a future change to either function automatically applies here too. The date is
 * converted to a `Date` before formatting -- passing the raw string can hit a legacy string-format
 * branch in the shared date formatter that ignores the timezone parameter entirely.
 */
function propertyCancellationText(rate: App.EnrichedPropertyRate, propertyTimezone: string, intl: IntlShape | undefined): string | undefined {
  const refundable = getPropertyRefundableStatus(rate.headlineCancellationPolicyType)
  const refundDescription = getRefundDescriptions(undefined, intl)[refundable]
  const shape = getPropertyCancellationLabelShape({
    refundable,
    headlineCancellationPolicyStart: rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined,
    propertyTimezone,
    shouldClarifyRefundIsHotelOnly: rate.includesFlights,
  })
  if (shape.kind !== 'date') {
    return shape.kind === 'no-date' && shape.hotelOnly ? `${refundDescription} (hotel only)` : refundDescription
  }
  return shape.hotelOnly
    ? `${refundDescription} until ${shape.formattedDate} (hotel only)`
    : `${refundDescription} until ${shape.formattedDate}`
}

/** Deliberately uncapped -- the rendered page shows every inclusion behind a "view more" toggle,
 * so the model should have the full list rather than an arbitrary summary-length cap. */
function inclusionTextList(inclusions: Array<App.OfferInclusion>): Array<string> | undefined {
  const descriptions = nonNullable(inclusions.map(inclusion => displayContextField(inclusion.description)))
  return descriptions.length ? descriptions : undefined
}

interface PackageCommercialAtoms {
  capacity?: string
  price?: string
  taxesAndFees?: string
  loyalty?: string
  cancellation?: string
  inclusions?: Array<string>
  deposit?: string
  availability?: string
  flightPriceStatus?: string
  flightPromotion?: string
}

/**
 * Mirrors the four distinct `cheapestFlightPrice` string reasons `PropertyRateOption` shows instead
 * of a price (a spinner for `'loading'`, or one of these messages) -- only relevant for a
 * flight-bundled rate, since `canShowCollapsedLeadPrice` is already true unconditionally otherwise.
 */
function propertyFlightPriceStatusText(rate: App.EnrichedPropertyRate): string | undefined {
  if (!rate.includesFlights) {
    return undefined
  }
  switch (rate.cheapestFlightPrice) {
    case 'loading':
      return 'Still calculating the flight-inclusive price'
    case 'unavailable':
    case 'originSameAsDestination':
      return 'Flight-inclusive price unavailable for the selected departure airport'
    case 'departureTooEarly':
      return 'Flight-inclusive price unavailable -- check-in is too soon to book flights online'
    case 'departureTooLate':
      return 'Flight-inclusive price unavailable -- check-in is too far in advance to book flights'
    case 0:
      return 'Flights not yet added to this rate'
    default:
      return undefined
  }
}

function packageSnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  targets: Array<SurfaceTargetRequest>
  selectedTargetIds: Array<string>
} {
  const atoms: Array<SurfaceAtomRequest> = []
  const targets: Array<SurfaceTargetRequest> = []
  // Every rate target carrying a `:selected` atom, regardless of room -- surfaced separately from
  // `atoms` so `buildOfferPageSnapshot` can protect the customer's actual pick(s) from truncation
  // in svc-trip's model-visible projection budget (a room-count budget can't guarantee a specific
  // rate survives otherwise; see `visibleTargetIds`'s ranking priority in that budget's sort).
  const selectedTargetIds: Array<string> = []
  // `rankHint` only controls this snapshot's own atom ordering (chunk content-hash stability, and
  // the order the read tool returns a target's facts in) -- it doesn't need to be collision-free
  // across targets, since sourcePath/atomId already break ties deterministically. A single counter
  // in call order is simpler than pre-deriving per-target offsets and gives the same result.
  let nextRankHint = 0
  const rankHint = () => nextRankHint++
  // The reducer's `viewedRateIdByRoomId` is keyed by raw room/rate ids, but `addRate` below only
  // ever compares against already-sanitised ids (via `withSnapshotId`). Sanitising this dictionary
  // once up front keeps the comparison consistent on both sides -- comparing raw against sanitised
  // would silently miss a real selection if an id ever contained a character `toOfferPageSnapshotId`
  // strips (today's ids don't, so this is currently a no-op, but it removes the landmine either way).
  const sanitisedViewedRateIdByRoomId = Object.fromEntries(
    Object.entries(source.selection.viewedRateIdByRoomId ?? {}).flatMap(([roomId, rateId]) => {
      const sanitisedRoomId = toOfferPageSnapshotId(roomId)
      const sanitisedRateId = toOfferPageSnapshotId(rateId)
      return sanitisedRoomId && sanitisedRateId ? [[sanitisedRoomId, sanitisedRateId]] : []
    }),
  )
  // `selection.rooms[].rateId` (a multi-room slot's committed rate assignment) and
  // `viewedRateIdByRoomId` (the last rate radio touched, a preview) are two independent
  // `OfferPageState` fields that can disagree for the same room. When a slot assignment exists,
  // it's the authoritative booking state -- prefer it over the preview so `:selected` can't
  // contradict what the customer actually committed to.
  const assignedRateIdByRoomId = Object.fromEntries(
    source.selection.rooms.flatMap(room => {
      const sanitisedRoomId = toOfferPageSnapshotId(room.roomId)
      const sanitisedRateId = toOfferPageSnapshotId(room.rateId)
      return sanitisedRoomId && sanitisedRateId ? [[sanitisedRoomId, sanitisedRateId]] : []
    }),
  )
  const addPackage = (
    id: string,
    roomName: string | undefined,
    packageName: string | undefined,
    description: string | undefined,
    commercial: PackageCommercialAtoms = {},
  ) => {
    const targetId = `package:${id}`
    targets.push(createTarget(targetId, 'package', packageName ?? roomName ?? 'Package', `offer.packages.${id}`, {
      dataClassification: 'commercial_surface',
      // Auto-focusable so the assistant can scroll to the package/room row. Stays
      // exact_fingerprint (volatile: price/availability tied to the current dates).
      modelUse: 'auto_focus_allowed',
    }))
    addTextAtom(atoms, `${targetId}:room`, 'room', 'Room', roomName, `offer.packages.${id}.room`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:summary`, 'package', 'Package', packageName, `offer.packages.${id}.summary`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:description`, 'package', 'Package details', description, `offer.packages.${id}.description`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:capacity`, 'capacity', 'Capacity', commercial.capacity, `offer.packages.${id}.capacity`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:price`, 'price', 'Price', commercial.price, `offer.packages.${id}.price`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:taxes-fees`, 'price', 'Taxes and fees', commercial.taxesAndFees, `offer.packages.${id}.taxes-fees`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:loyalty`, 'loyalty', 'Loyalty points earn', commercial.loyalty, `offer.packages.${id}.loyalty`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:cancellation`, 'policy', 'Cancellation policy', commercial.cancellation, `offer.packages.${id}.cancellation`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:inclusions`, 'inclusions', 'Inclusions', commercial.inclusions?.join('; '), `offer.packages.${id}.inclusions`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:deposit`, 'payment', 'Payment options', commercial.deposit, `offer.packages.${id}.deposit`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:availability`, 'availability', 'Availability', commercial.availability, `offer.packages.${id}.availability`, targetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${targetId}:flight-promotion`, 'promotion', 'Flight promotion', commercial.flightPromotion, `offer.packages.${id}.flight-promotion`, targetId, rankHint(), 'commercial_surface')
  }
  const addRate = (
    roomId: string,
    rateId: string,
    rateLabel: string | undefined,
    commercial: PackageCommercialAtoms = {},
  ): string => {
    const roomTargetId = `package:${roomId}`
    const rateTargetId = `room-package:${roomId}:${rateId}`
    targets.push(createTarget(rateTargetId, 'rate', rateLabel ?? 'Rate', `offer.packages.${roomId}.rates.${rateId}`, {
      dataClassification: 'commercial_surface',
      parentTargetId: roomTargetId,
      // Auto-focusable, with the room row as the BE-approved fallback when the exact rate is not
      // mounted (e.g. Property's merged-rate-group shows only the selected rate). Stays
      // exact_fingerprint — volatile, date/price-dependent.
      modelUse: 'auto_focus_allowed',
      fallbackTargetId: roomTargetId,
    }))
    addTextAtom(atoms, `${rateTargetId}:price`, 'price', 'Price', commercial.price, `offer.packages.${roomId}.rates.${rateId}.price`, rateTargetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${rateTargetId}:taxes-fees`, 'price', 'Taxes and fees', commercial.taxesAndFees, `offer.packages.${roomId}.rates.${rateId}.taxes-fees`, rateTargetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${rateTargetId}:loyalty`, 'loyalty', 'Loyalty points earn', commercial.loyalty, `offer.packages.${roomId}.rates.${rateId}.loyalty`, rateTargetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${rateTargetId}:cancellation`, 'policy', 'Cancellation policy', commercial.cancellation, `offer.packages.${roomId}.rates.${rateId}.cancellation`, rateTargetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${rateTargetId}:inclusions`, 'inclusions', 'Inclusions', commercial.inclusions?.join('; '), `offer.packages.${roomId}.rates.${rateId}.inclusions`, rateTargetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${rateTargetId}:deposit`, 'payment', 'Payment options', commercial.deposit, `offer.packages.${roomId}.rates.${rateId}.deposit`, rateTargetId, rankHint(), 'commercial_surface')
    addTextAtom(atoms, `${rateTargetId}:flight-price-status`, 'price', 'Flight-inclusive price status', commercial.flightPriceStatus, `offer.packages.${roomId}.rates.${rateId}.flight-price-status`, rateTargetId, rankHint(), 'commercial_surface')
    // Only emitted once the customer has explicitly picked a rate via `PropertyRateOptionMerged`'s
    // radio group (spp_property/lme_property) -- there's no atom for the untouched default rate a
    // room's radio starts checked on, since replicating that selection algorithm here isn't needed
    // to fix the bug this exists for (a stale claim once the customer changes their pick mid-chat)
    // and risks asserting a default that doesn't actually match the page. No atom is a safe "we
    // don't have an explicit choice on record," not a claim that nothing is selected.
    const assignedRateId = assignedRateIdByRoomId[roomId]
    const isSelectedRate = assignedRateId ? assignedRateId === rateId : sanitisedViewedRateIdByRoomId[roomId] === rateId
    if (isSelectedRate) {
      addTextAtom(atoms, `${rateTargetId}:selected`, 'rate', 'Selected rate', 'Selected by the customer', `offer.packages.${roomId}.rates.${rateId}.selected`, rateTargetId, rankHint(), 'personalised_surface')
      selectedTargetIds.push(rateTargetId)
    }
    return rateTargetId
  }

  switch (source.variant) {
    case 'flash': {
      // Room-type-level urgency labels (rooms-left, booked-recently) are rendered once per room
      // type on the real page (`FlashHotelSameRoomOption`'s shared `urgencyLabels` area), not once
      // per rate -- precompute the displayed room count per group here, matching the real
      // component's own `isMultiRoom ? min(...) : cheapestPackageView.availableRooms` choice, and
      // attach the labels only to the first-processed rate in each group below.
      // A charter bundle forces flights on regardless of the customer's own toggle -- mirrors
      // `getIsFlightsSelected`'s own derivation, but `source.charterBundleRoute` here is already
      // the *effective* route (`FlashHotelContainer.tsx` resolves it via
      // `getEffectiveCharterBundleRoute` before it ever reaches this source object).
      const isFlightsSelected = !!source.charterBundleRoute || source.selection.flights
      const isFlightPriceReady = !!source.effectiveFlightPrice && source.effectiveFlightPrice > 0
      // Suppresses price/loyalty entirely while a brand-new duration/date selection's live fare is
      // still resolving, rather than reporting a confidently-wrong hotel-only total/earn figure
      // for the fraction of a second before it settles -- mirrors the `isRatesLoading` gate already
      // applied to the Property variant below.
      const isFlightPriceStillLoading = isFlightsSelected && !!source.effectiveFlightPriceLoading
      // `total > 0` preserves the "not applicable" 0 sentinel `hotelMemberTotal`/`hotelValue` can
      // legitimately hold (no member price / no strikethrough value for this package) -- adding a
      // flight price on top of that 0 would otherwise fabricate a value that was never there.
      const withEffectiveFlightPrice = (total: number) =>
        (isFlightsSelected && isFlightPriceReady && total > 0) ? total + source.effectiveFlightPrice! : total
      // With no matching check-in date, `effectiveFlightPrice` is always 0 (charter routes are
      // excluded from `getEffectiveHotelFlightPrice`'s cached-fare fallback) -- `withEffectiveFlightPrice`
      // would otherwise silently degrade to a hotel-only total. Matches
      // `FlashHotelOptionPriceDetails.tsx`'s `shouldUseFallbackPricing` branch: use the cheapest
      // jointly-available hotel+flight date instead (`source.charterFallbackPricingByPackage`,
      // populated per-package by `FlashPackageCharterFallbackPricingRegistration`).
      const isFallbackPricingActive = !!source.shouldUseCharterFallbackPricing
      // `packageView.hotelTotal`/`hotelMemberTotal`/`hotelValue` (and the fallback-pricing
      // equivalents above) are always whole-party totals -- matches the real page's own
      // `getPriceDisplayFunction`, which divides by traveller count when the customer has
      // switched the page to "Price per person" (`selection.priceMode`), rather than leaving the
      // assistant's figure contradicting what's on screen.
      const priceDisplay = getPriceDisplayFunction(source.selection.priceMode, source.selection.rooms)
      const priceUnitLabel = getOfferPageSnapshotPriceUnitLabel(source.selection.priceMode)
      // Matches every real render site's shared suppression exactly: the "Return for FREE with LuxPlus+"
      // badge never shows once a feeder-origin airport is selected, regardless of the
      // per-package/fallback value below.
      const isReturnFareFreeSuppressed = !!source.feederOriginAirportCode

      const isMultiRoom = source.selection.rooms.length > 1
      const roomTypeGroups = new Map<string, Array<App.OfferPackageOptionView>>()
      source.packageViews.forEach((packageView) => {
        const roomTypeId = packageView.package.roomType.id
        const group = roomTypeGroups.get(roomTypeId) ?? []
        group.push(packageView)
        roomTypeGroups.set(roomTypeId, group)
      })
      const displayedAvailableRoomsByRoomTypeId = new Map<string, number>()
      roomTypeGroups.forEach((group, roomTypeId) => {
        const displayed = isMultiRoom
          ? min(group, packageView => packageView.availableRooms ?? 0)?.availableRooms
          : min(group, packageView => packageView.hotelPrice)?.availableRooms
        if (displayed !== undefined) {
          displayedAvailableRoomsByRoomTypeId.set(roomTypeId, displayed)
        }
      })
      const roomTypeIdsWithUrgencyEmitted = new Set<string>()

      uniqueBy(
        withSnapshotId(source.packageViews, packageView => packageView.package.uniqueKey),
        pair => pair.id,
      ).forEach(({ item: packageView, id }) => {
        const pkg = packageView.package
        const roomTypeId = pkg.roomType.id
        const includeUrgencyLabels = !roomTypeIdsWithUrgencyEmitted.has(roomTypeId)
        roomTypeIdsWithUrgencyEmitted.add(roomTypeId)
        const [rawFlashInclusions, rawFlashBonusInclusions, flashLuxPlusInclusions] = extractPkgInclusions(pkg, {
          checkIn: source.selection.checkInDate,
          checkOut: source.selection.checkOutDate,
          luxPlusTier: source.luxPlusInclusionsTier,
        })
        // Matches the real tile exactly: manually-written "valid for N guests" lines are stripped
        // (never `luxPlusInclusions`, which the real tile never applies this to either) once the
        // package has real structured validity data to fall back on instead.
        const shouldShowPackageInclusionsValidityText = !!source.isInclusionsValidityEnabled &&
          pkg.showInclusionsValidityTag &&
          hasStructuredPackageInclusionsValidityText(pkg.roomPolicyDescription)
        const flashInclusions = getInclusionsWithoutPackageValidityText(rawFlashInclusions, shouldShowPackageInclusionsValidityText)
        const flashBonusInclusions = getInclusionsWithoutPackageValidityText(rawFlashBonusInclusions, shouldShowPackageInclusionsValidityText)
        // `undefined` here covers both "still fetching" and "resolved, but this package has no
        // available charter-bundle date for the current occupancy/nights" -- the ternary below
        // already falls back to the hotel-only total/loyalty in either case, matching
        // `FlashHotelOptionPriceDetails.tsx`'s own `charterPricingToDisplay` default branch exactly
        // ("Default to hotel pricing (loading state or error)"), rather than hiding price/loyalty
        // for what can be a permanent no-availability state.
        const charterFallbackPricing = isFallbackPricingActive ? source.charterFallbackPricingByPackage?.[pkg.uniqueKey] : undefined
        const isPackagePriceStillResolving = isFlightPriceStillLoading
        const applyPriceDisplay = (amount: number | undefined) => amount === undefined ? undefined : priceDisplay(amount)
        const packagePriceSummary = charterFallbackPricing ? buildDisplayedPriceSummary({
          memberTotal: applyPriceDisplay(charterFallbackPricing.memberPrice),
          standardTotal: applyPriceDisplay(charterFallbackPricing.price),
          valuedUpTo: applyPriceDisplay(charterFallbackPricing.value),
          showValuedUpTo: pkg.shouldDisplayValue,
          unitLabel: priceUnitLabel,
        }, source.priceFormatContext) : buildDisplayedPriceSummary({
          memberTotal: priceDisplay(withEffectiveFlightPrice(packageView.hotelMemberTotal)),
          standardTotal: priceDisplay(withEffectiveFlightPrice(packageView.hotelTotal)),
          valuedUpTo: priceDisplay(withEffectiveFlightPrice(packageView.pricing.value)),
          showValuedUpTo: pkg.shouldDisplayValue,
          unitLabel: priceUnitLabel,
        }, source.priceFormatContext)
        addPackage(
          id,
          getHotelRoomTypeName(pkg.roomType),
          pkg.name,
          // `pkg.roomType.description` is the real visible description text; `pkg.description` is
          // inclusions-summary text (already covered by the inclusions atom below).
          pkg.roomType.description,
          {
            // No `capacities` -- Flash rooms don't have a rendered "Sleeps N" figure, so this
            // atom is intentionally bed/bedroom/size only for this variant.
            capacity: capacityText({
              capacities: undefined,
              // `?? []` matches the real room-details extractor's undefined-safety exactly --
              // without it, an offer with no `bedGroups` would throw here instead of silently
              // showing no beds text like the real tile does.
              bedDescription: (pkg.roomType.bedGroups ?? []).map(group => group.description).join(' OR '),
              size: pkg.roomType.sizeSqm ? `${pkg.roomType.sizeSqm}m²` : undefined,
              bedrooms: pkg.roomType.numberOfBedrooms,
            }),
            price: isPackagePriceStillResolving ? undefined : packagePriceSummary,
            // No `taxesAndFees` -- Flash never breaks a taxes/fees figure out into its own value,
            // unlike Bedbank/Property, so there's no dollar amount to atomise for this variant.
            loyalty: isPackagePriceStillResolving ? undefined : formatOfferPageLoyaltyEarnSummary(source.roomPackageLoyaltyEarnTotals?.[pkg.uniqueKey], source.priceFormatContext.regionCode),
            cancellation: flashCancellationText(
              pkg,
              source.selection.checkInDate,
              source.charterBundleRoute ? source.charterDepartDate : undefined,
              source.offer.property?.timezoneOffset,
              source.isCharterModeEnabled ?? false,
              source.charterBundleRoute,
            ),
            // Matches the real tile's rendered order exactly: LuxPlus+ tier inclusions in their own
            // section first, then bonus inclusions, then base inclusions.
            inclusions: inclusionTextList([...flashLuxPlusInclusions, ...flashBonusInclusions, ...flashInclusions]),
            deposit: flashDepositText(source.offer, packageView),
            availability: flashAvailabilityText(packageView, {
              checkIn: source.selection.checkInDate,
              displayedAvailableRooms: displayedAvailableRoomsByRoomTypeId.get(roomTypeId),
              includeUrgencyLabels,
            }),
            // Matches `FlashHotelOptionPriceDetails.tsx`'s own priority exactly:
            // `packageView.hasReturnFareFree ?? (fallback mode ? charterFallbackPricing.hasReturnFareFree
            // : offer-wide hook value)`. The package's own field wins whenever it's *defined*
            // (true or false), regardless of fallback mode -- only when it's `undefined` ("unknown,"
            // not "no") does the fallback-or-live value apply. `charterFallbackPricing.hasReturnFareFree`
            // is the only real answer in fallback-pricing mode when the package itself doesn't say --
            // the offer-wide hook value needs a resolved dated journey, so it's always false in that
            // state even though the real page has its own answer here.
            // Only suppressed while still resolving when the package's own field is undefined AND
            // the fallback/live source it would fall through to hasn't resolved either -- unlike
            // price/loyalty, `packageView.hasReturnFareFree` needs no fetch at all, so a defined
            // value here shows immediately even if this package's fallback price is still pending.
            flightPromotion: !(packageView.hasReturnFareFree === undefined &&
              (isFallbackPricingActive ? !charterFallbackPricing : isFlightPriceStillLoading)) &&
              (packageView.hasReturnFareFree ?? (isFallbackPricingActive ? charterFallbackPricing?.hasReturnFareFree : source.hasReturnFareFree)) &&
              !isReturnFareFreeSuppressed
              ? 'Return for FREE with LuxPlus+'
              : undefined,
          },
        )
      })
      break
    }
    case 'bedbank':
      uniqueBy(
        withSnapshotId(source.packageViews, packageView => packageView.package.roomId),
        pair => pair.id,
      ).forEach(({ item: packageView, id: roomId }) => {
        const room = packageView.package
        const bedbankFeatures = new Map(room.features.map(feature => [feature.id, feature.text]))
        addPackage(roomId, room.name, undefined, bedbankPlainDescription(room.description), {
          // `bedrooms: undefined` -- no Bedbank UI anywhere renders a bedroom count for these
          // rooms, so surfacing `room.numberOfBedrooms` here would disclose data the real page
          // never shows.
          capacity: capacityText({
            capacities: room.capacities.combinations,
            bedDescription: bedbankFeatures.get('bed'),
            size: bedbankFeatures.get('dimension'),
            bedrooms: undefined,
          }),
          availability: bedbankAvailabilityText(packageView),
        })
        uniqueBy(
          // `effectiveRates`, not `rates` -- once the customer has flights selected, the real page
          // renders flight-bundle rates (via `combineBedbankRates`), not the hotel-only ones.
          withSnapshotId(packageView.effectiveRates, rate => rate.id),
          pair => pair.id,
        ).forEach(({ item: rate, id: rateId }) => {
          const promotion = packageView.promotions[rate.promotionId ?? '']
          const pricing = getBedbankDisplayPricing(rate)
          // `??` (not a truthy/length check): matches the real tile exactly -- an explicit empty
          // `promotion.inclusions` array wins over `rate.inclusions` when a promotion applies,
          // falling through to the `rateInclusionsLong` markdown fallback below, same as the page.
          const promotionInclusions = promotion?.inclusions ?? rate.inclusions
          const rateTargetId = addRate(roomId, rateId, rate.boardCode, {
            price: buildDisplayedPriceSummary({
              memberTotal: pricing.memberTotal,
              standardTotal: pricing.total,
              valuedUpTo: pricing.value,
            }, source.priceFormatContext),
            taxesAndFees: nonEmptyString(formatCurrency(rate.totals.taxesAndFees, source.priceFormatContext.currencyCode, source.priceFormatContext.regionCode)),
            loyalty: formatOfferPageLoyaltyEarnSummary(source.roomPackageLoyaltyEarnTotals?.[rate.id], source.priceFormatContext.regionCode),
            cancellation: bedbankCancellationText(rate, promotion, !!source.selection.checkInDate && !!source.selection.checkOutDate),
            // When a promotion applies, the real tile shows the promotion's own inclusions (or its
            // long-form markdown as a fallback) instead of the rate's base inclusions.
            // `rateInclusionsLong` is markdown (the real tile renders it via `MarkdownRender`) --
            // strip it to plain text before splitting into lines, matching the same "feed the
            // model plain text, not markup" convention already used for fine-print/body atoms.
            inclusions: promotionInclusions.length
              ? inclusionTextList(promotionInclusions)
              // `stripMarkdown` inserts blank-line separators between blocks (e.g. a paragraph
              // followed by a list) that `.split('\n')` alone would turn into empty array entries
              // -- filtered out here so `commercial.inclusions?.join('; ')` below never renders a
              // stray "; ;".
              : nonEmptyString(stripMarkdown(promotion?.rateInclusionsLong ?? ''))?.split('\n').filter(line => line.trim().length > 0),
            deposit: bedbankDepositText(rate, {
              isPropertyCollectEnabled: source.isPropertyCollectEnabled,
              flightsEnabled: source.flightsEnabled,
              isFlightsSelected: source.selection.flights,
              hasFlightOrigin: !!source.selection.airportCode,
            }),
          })
          addTextAtom(atoms, `${rateTargetId}:board`, 'rate', 'Meal plan', rate.boardCode, `offer.packages.${roomId}.rates.${rateId}.board`, rateTargetId, rankHint(), 'commercial_surface')
        })
      })
      break
    case 'lme_property':
    case 'spp_property': {
      // `FETCH_PROPERTY_RATES` deliberately keeps the previous `rates` payload visible while a
      // refetch is in flight (see `offer.ts`'s reducer comment), so `roomRateView.rates` here can
      // be stale during that window. Room identity/capacity/availability atoms below aren't sourced
      // from that refetch-affected data, so they stay unconditional; only the commercial fields
      // that come from `rate` itself (price, tax, loyalty, cancellation, deposit) are suppressed,
      // matching the `packages.rates_status` manifest fact so atoms and fact agree with each other.
      const isRatesLoading = source.ratesStatus === 'loading'
      uniqueBy(
        withSnapshotId(source.roomRateViews, roomRateView => roomRateView.roomId),
        pair => pair.id,
      ).forEach(({ item: roomRateView, id: roomId }) => {
        const room = roomRateView.propertyRoom
        const roomTargetId = `package:${roomId}`
        targets.push(createTarget(roomTargetId, 'room', room.name, `offer.packages.${roomId}`, {
          dataClassification: 'commercial_surface',
          // Auto-focusable so the assistant can scroll to the room row (also the approved fallback
          // for its rate targets). Stays exact_fingerprint — volatile, date/availability-dependent.
          modelUse: 'auto_focus_allowed',
        }))
        addTextAtom(atoms, `${roomTargetId}:summary`, 'room', 'Room', room.name, `offer.packages.${roomId}.summary`, roomTargetId, rankHint(), 'commercial_surface')
        addTextAtom(atoms, `${roomTargetId}:description`, 'room', 'Room details', room.description, `offer.packages.${roomId}.description`, roomTargetId, rankHint(), 'commercial_surface')
        // Single-unit (villa/rental) properties hide the per-room Sleeps/bedrooms figures in
        // favour of the whole-property one built by `singleUnitCapacityText` above.
        const isSingleUnit = !!source.property.singleUnit
        addTextAtom(atoms, `${roomTargetId}:capacity`, 'capacity', 'Capacity', capacityText({
          capacities: isSingleUnit ? undefined : room.capacities,
          bedDescription: room.bedGroupDescriptions?.join(' OR '),
          size: room.size,
          bedrooms: isSingleUnit ? undefined : room.bedrooms,
        }), `offer.packages.${roomId}.capacity`, roomTargetId, rankHint(), 'commercial_surface')
        addTextAtom(atoms, `${roomTargetId}:availability`, 'availability', 'Availability', propertyRoomAvailabilityText(roomRateView) ?? propertyRoomUrgencyText(
          roomRateView,
          !!source.selection.checkInDate,
          !!source.isSppRoomUrgencyEnabled,
          source.priceFormatContext,
        ), `offer.packages.${roomId}.availability`, roomTargetId, rankHint(), 'commercial_surface')
        uniqueBy(
          withSnapshotId(roomRateView.rates, rate => rate.rateId),
          pair => pair.id,
        ).forEach(({ item: rate, id: rateId }) => {
          // Only LPC rates carry a matching offer package (for `roomPolicyDescription`) -- mirrors
          // `PropertyRateOption`'s own `lpcPackageId`/`matchingPackage` resolution exactly.
          const lpcPackageId = rate.service.type === 'lpc' ? rate.service.packageId : undefined
          const matchingPackage = lpcPackageId
            ? source.lpcOffer?.packages.find(pkg => pkg.id === lpcPackageId || pkg.lePackageId === lpcPackageId)
            : undefined
          const hasPackageInclusions = !!(
            rate.luxPlusInclusions?.length ||
            rate.bonusInclusions?.length ||
            rate.bundleInclusions?.length ||
            rate.inclusions?.length
          )
          // Matches the real tile exactly: manually-written "valid for N guests" lines are stripped
          // (never `luxPlusInclusions`, which the real tile never applies this to either) once the
          // package has real structured validity data to fall back on instead.
          const shouldShowInclusionsValidityText = rate.service.type === 'lpc' &&
            !!source.isInclusionsValidityEnabled &&
            !!matchingPackage?.showInclusionsValidityTag &&
            hasPackageInclusions &&
            hasStructuredPackageInclusionsValidityText(matchingPackage?.roomPolicyDescription)
          // Matches `PropertyRateOption`'s own price-row gate exactly: a flight-bundled rate hides
          // its price/loyalty-earn figures (showing a spinner or a reason message instead) until a
          // real flight-inclusive price has resolved.
          const canShowFlightInclusivePrice = canShowCollapsedLeadPrice(rate)
          const rateTargetId = addRate(roomId, rateId, rate.name, {
            price: isRatesLoading || !canShowFlightInclusivePrice ? undefined : buildDisplayedPriceSummary({
              memberTotal: rate.luxPlusTotals?.displayPrice,
              standardTotal: rate.totals.displayPrice,
              valuedUpTo: rate.totals.valuedUpToPrice,
            }, source.priceFormatContext),
            taxesAndFees: isRatesLoading ? undefined : nonEmptyString(formatCurrency(rate.totals.taxesAndFees, source.priceFormatContext.currencyCode, source.priceFormatContext.regionCode)),
            loyalty: isRatesLoading || !canShowFlightInclusivePrice ? undefined : formatOfferPageLoyaltyEarnSummary(source.roomPackageLoyaltyEarnTotals?.[rate.rateId], source.priceFormatContext.regionCode),
            cancellation: isRatesLoading ? undefined : propertyCancellationText(rate, source.property.location.timezone, source.intl),
            inclusions: inclusionTextList(getOrderedPropertyInclusions({
              luxPlusInclusions: rate.luxPlusInclusions ?? [],
              bonusInclusions: getInclusionsWithoutPackageValidityText(rate.bonusInclusions ?? [], shouldShowInclusionsValidityText),
              bundleInclusions: getInclusionsWithoutPackageValidityText(rate.bundleInclusions ?? [], shouldShowInclusionsValidityText),
              inclusions: getInclusionsWithoutPackageValidityText(rate.inclusions ?? [], shouldShowInclusionsValidityText),
            })),
            deposit: isRatesLoading ? undefined : propertyDepositText(rate),
            flightPriceStatus: canShowFlightInclusivePrice ? undefined : propertyFlightPriceStatusText(rate),
          })
          addTextAtom(atoms, `${rateTargetId}:summary`, 'rate', 'Rate', rate.name, `offer.packages.${roomId}.rates.${rateId}.summary`, rateTargetId, rankHint(), 'commercial_surface')
        })
      })
      break
    }
    default:
      assertUnreachable(source)
  }

  if (source.selection.rooms.length > 1) {
    const targetIds = new Set(nonNullable(targets.map(target => target.targetId)))
    withSnapshotId(source.selection.rooms, room => room.id).forEach(({ item: room, id: roomSlotId }, index) => {
      const resolvedTargetId = resolveRoomSlotTargetId(source, room, targetIds)
      const occupancyText = resolvedTargetId ? formatAssistantRoomOccupancyText(room) : undefined
      if (!resolvedTargetId || !occupancyText) {
        return
      }

      const atom = createAtom(
        `booking:room:${roomSlotId}:selection`,
        'booking_selection',
        'Room selection',
        formatAssistantRoomLabel(index + 1, occupancyText),
        `offer.booking.rooms.${roomSlotId}.selection`,
        resolvedTargetId,
        rankHint(),
        'personalised_surface',
      )
      if (atom) {
        atoms.push(atom)
      }
    })
  }

  return { atoms, targets, selectedTargetIds }
}

export function ratesStatusFact(isLoading: boolean, hasError: boolean): 'loading' | 'unavailable' | undefined {
  if (isLoading) {
    return 'loading'
  }

  if (hasError) {
    return 'unavailable'
  }

  return undefined
}

function packageAvailabilityFacts(source: OfferPageAssistantSource): Array<SurfaceManifestFactRequest> {
  const datesSelected = !!source.selection.checkInDate && !!source.selection.checkOutDate
  // Loyalty totals apply to all three variants (unlike `rates_status`, which is Property-only), so
  // this is computed once and appended to every branch below.
  const loyaltyStatusFact = createFact('packages.loyalty_status', source.loyaltyStatus, 'commercial_surface')

  switch (source.variant) {
    case 'lme_property':
    case 'spp_property': {
      // Mirrors `isSoldOutForDatesAndOccupancies` (every room's rates are unavailable for the
      // current dates/occupancy) -- that selector only drives analytics today (the real "SOLD OUT"
      // banner is currently unreachable dead code, `isPropertyAvailableForPurchase` is hardcoded
      // `true`), so this is the closest honest "is anything actually bookable" signal available.
      const isSoldOutForDates = datesSelected &&
        source.roomRateViews.length > 0 &&
        source.roomRateViews.every(view => !view.datesAvailable)
      return nonNullable([
        createFact('packages.rates_status', source.ratesStatus, 'commercial_surface'),
        createFact('packages.sold_out_for_dates', isSoldOutForDates ? 'true' : undefined, 'commercial_surface'),
        loyaltyStatusFact,
      ])
    }
    case 'bedbank': {
      // Mirrors the real "Sold out on your dates" banner: every room has zero available rates
      // while dates are selected.
      const isSoldOutForDates = datesSelected &&
        source.packageViews.length > 0 &&
        source.packageViews.every(view => !view.available)
      return nonNullable([
        createFact('packages.sold_out_for_dates', isSoldOutForDates ? 'true' : undefined, 'commercial_surface'),
        loyaltyStatusFact,
      ])
    }
    case 'flash':
      return nonNullable([loyaltyStatusFact])
    default:
      return assertUnreachable(source)
  }
}

function policySnapshotSources(source: OfferPageAssistantSource): {
  bedbankOffer?: App.BedbankOffer
  hotelOffer?: App.HotelOffer
  property?: App.Property.Property
} {
  switch (source.variant) {
    case 'flash':
      return { hotelOffer: source.offer }
    case 'bedbank':
      return { bedbankOffer: source.offer }
    case 'lme_property':
    case 'spp_property':
      return { property: source.property }
    default:
      return assertUnreachable(source)
  }
}

/**
 * Fine-print section titles can collide (a bedbank offer's hardcoded section
 * titles and its arbitrary supplier-controlled policy keys are merged with no
 * upstream dedupe). Breaking the collision with a deterministic suffix keeps
 * both sections addressable under distinct target ids instead of silently
 * losing one to a duplicate targetId — the ADR requires canonical construction
 * to never drop safe, distinct rendered content.
 */
function uniqueFinePrintSectionKey(baseKey: string, usedKeys: Set<string>): string {
  if (!usedKeys.has(baseKey)) {
    return baseKey
  }

  let suffix = 2
  while (usedKeys.has(`${baseKey}-${suffix}`)) {
    suffix += 1
  }
  return `${baseKey}-${suffix}`
}

/**
 * Row keys where multiple sections collapse onto one shared target (see `targetIdByRowKey`
 * below) need a fixed label matching the real rendered row's own heading -- not whichever
 * section happens to claim the target first, which would otherwise describe the row by one
 * arbitrary, key-order-dependent supplier policy's title instead of the row itself.
 */
const GROUPED_FINE_PRINT_ROW_LABELS: Partial<Record<string, string>> = {
  [BEDBANK_THINGS_TO_KNOW_ROW_KEYS.policies]: 'Policies',
}

function policySnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const sections = buildOfferPageFinePrintSectionsFromSources(policySnapshotSources(source), { normalization: 'snapshot' })
  const atoms: Array<SurfaceAtomRequest> = []
  const targets: Array<SurfaceTargetRequest> = sections.length ? [
    createTarget('fine-print:root', 'section', 'Things to know', 'offer.policies', {
      dataClassification: 'commercial_surface',
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }),
  ] : []

  const usedSectionKeys = new Set<string>()
  // Sections sharing a real row `key` (e.g. every bedbank supplier policy collapsing onto the
  // real page's single "Policies" row) group onto one target -- keyed separately from
  // `usedSectionKeys`, which stays per-section so atom ids remain unique and unaffected.
  const targetIdByRowKey = new Map<string, string>()
  for (const [index, section] of sections.entries()) {
    const baseSectionKey = toOfferPageSnapshotId(
      section.title?.toLowerCase()?.replace(/\s+/g, '-') ?? `section-${index + 1}`,
    )
    if (!baseSectionKey) {
      continue
    }
    const sectionKey = uniqueFinePrintSectionKey(baseSectionKey, usedSectionKeys)
    usedSectionKeys.add(sectionKey)

    // The real rendered row key (e.g. `cancellation`, matching `ThingsToKnow.tsx`'s own hardcoded
    // id) if one exists, else fall back to the auto-slugified title -- so content the real
    // component never labelled with a stable key still gets its own addressable target.
    const rowKey = toOfferPageSnapshotId(section.key)
    let targetId = rowKey ? targetIdByRowKey.get(rowKey) : undefined
    if (!targetId) {
      targetId = `fine-print:${rowKey ?? sectionKey}`
      const targetLabel = (rowKey && GROUPED_FINE_PRINT_ROW_LABELS[rowKey]) || section.title || 'Policy'
      targets.push(createTarget(targetId, 'policy', targetLabel, `offer.policies.${rowKey ?? sectionKey}`, {
        dataClassification: 'commercial_surface',
        parentTargetId: 'fine-print:root',
        fallbackTargetId: 'fine-print:root',
        modelUse: 'auto_focus_allowed',
      }))
      if (rowKey) {
        targetIdByRowKey.set(rowKey, targetId)
      }
    }
    addTextAtom(atoms, `fine-print:${sectionKey}:content`, 'policy', section.title ?? 'Policy', section.body, `offer.policies.${sectionKey}.content`, targetId, index, 'commercial_surface')
  }

  return { atoms, targets }
}

function addMissingAtomTargets(
  atoms: Array<SurfaceAtomRequest>,
  existingTargets: Array<SurfaceTargetRequest>,
): Array<SurfaceTargetRequest> {
  const targetIds = new Set(existingTargets.map(target => target.targetId))
  const targets = [...existingTargets]

  for (const atom of atoms) {
    if (!(atom.targetId && atom.label && atom.sourcePath) || targetIds.has(atom.targetId)) {
      continue
    }

    targets.push(createTarget(atom.targetId, atom.kind ?? 'content', atom.label, atom.sourcePath, {
      dataClassification: atom.dataClassification,
      parentTargetId: atom.parentTargetId ?? undefined,
    }))
    targetIds.add(atom.targetId)
  }

  return sortTargets(targets)
}

function belongsToSourcePathNamespace(sourcePath: string | null | undefined, root: string): boolean {
  return sourcePath === root || sourcePath?.startsWith(`${root}.`) === true
}

function belongsToPoliciesChunk(sourcePath: string | null | undefined): boolean {
  return belongsToSourcePathNamespace(sourcePath, 'offer.policies')
}

function belongsToPropertyChunk(sourcePath: string | null | undefined): boolean {
  return belongsToSourcePathNamespace(sourcePath, 'offer.overview') ||
    belongsToSourcePathNamespace(sourcePath, 'offer.location') ||
    sourcePath?.startsWith('offer.property.') === true
}

function buildAtomChunk(chunkId: OfferChunkId, atoms: Array<SurfaceAtomRequest>): AssistantSurfaceSnapshotChunk {
  return {
    chunkId,
    kind: 'atoms',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'atoms', atoms }),
    atoms,
  }
}

function buildTargetChunk(chunkId: OfferChunkId, targets: Array<SurfaceTargetRequest>): AssistantSurfaceSnapshotChunk {
  return {
    chunkId,
    kind: 'targets',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'targets', targets }),
    targets,
  }
}

function buildCommittedStateChunk(
  committedState: Array<SurfaceRecentCommittedStateRequest>,
): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'committed',
    kind: 'recent_committed_state',
    contentHash: assistantSurfaceSnapshotChunkContentHash({
      kind: 'recent_committed_state',
      recentCommittedState: committedState,
    }),
    recentCommittedState: committedState,
  }
}

function buildCapabilityChunk(): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'offer',
    kind: 'capabilities',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'capabilities', capabilities }),
    capabilities,
  }
}

function normaliseContributionAtoms(contributions: Array<OfferPageSnapshotContribution>): Array<SurfaceAtomRequest> {
  return contributions
    .flatMap(contribution => contribution.atoms.flatMap(atom => {
      const atomId = atom.atomId ? toOfferPageSnapshotId(atom.atomId) : undefined
      const targetId = atom.targetId ? toOfferPageSnapshotId(atom.targetId) : undefined
      const parentTargetId = atom.parentTargetId ? toOfferPageSnapshotId(atom.parentTargetId) : undefined
      const label = atom.label ? toOfferPageSnapshotLabel(atom.label) : undefined
      const sourcePath = atom.sourcePath ? toOfferPageSnapshotSourcePath(atom.sourcePath) : undefined
      if (!atomId || !targetId || !label || !sourcePath) {
        return []
      }

      const segments = splitOfferPageSnapshotAtomText(atom.displayText)
      return segments.map((displayText, index) => {
        const segmentSuffix = segments.length > 1 ? `:seg-${index + 1}` : ''
        return {
          ...atom,
          atomId: `${atomId}${segmentSuffix}`,
          targetId,
          ...(parentTargetId ? { parentTargetId } : {}),
          label,
          displayText,
          sourcePath: segmentSuffix ? `${sourcePath}${segmentSuffix}` : sourcePath,
          rankHint: (atom.rankHint ?? 0) + index,
        }
      })
    }))
    .filter(atom => atom.atomId && atom.label && atom.displayText && atom.sourcePath)
}

function normaliseContributionCommittedState(
  contributions: Array<OfferPageSnapshotContribution>,
): Array<SurfaceRecentCommittedStateRequest> {
  return contributions
    .flatMap(contribution => contribution.recentCommittedState)
    .flatMap(state => {
      const key = state.key ? toOfferPageSnapshotId(state.key) : undefined
      const targetId = state.targetId ? toOfferPageSnapshotId(state.targetId) : undefined
      const label = state.label ? toOfferPageSnapshotLabel(state.label) : undefined
      const sourcePath = state.sourcePath ? toOfferPageSnapshotSourcePath(state.sourcePath) : undefined
      if (!key || !state.kind || !label || !sourcePath) {
        return []
      }

      const segments = splitOfferPageSnapshotAtomText(state.displayText)
      return segments.map((displayText, index) => {
        const segmentSuffix = segments.length > 1 ? `:seg-${index + 1}` : ''
        return {
          ...state,
          key: `${key}${segmentSuffix}`,
          ...(targetId ? { targetId } : {}),
          label,
          displayText,
          sourcePath: `${sourcePath}${segmentSuffix}`,
        }
      })
    })
    .filter(state => state.key && state.kind && state.label && state.displayText && state.sourcePath)
}

/**
 * The 5 content chunk namespaces (booking/packages/policies/property/reviews)
 * are matched by sourcePath prefix with no catch-all bucket, and the 12 chunk
 * identities are immutable within schemaVersion 1 — there is no "misc" chunk
 * to add one. If a future atom/target's sourcePath doesn't match any
 * namespace, it would otherwise silently vanish from every chunk. This is a
 * low-cardinality count check only — no atom/target prose is logged. Fails
 * loud rather than warn-and-continue: the caller (`resolveTurnAuthority`)
 * already catches synchronous throws from snapshot building and degrades to
 * `degradedSurfaceIdentity`, so this only blocks publishing an incomplete
 * snapshot -- it never crashes the turn.
 */
function assertChunkCoverageComplete(
  atoms: Array<SurfaceAtomRequest>,
  targets: Array<SurfaceTargetRequest>,
  chunks: Array<AssistantSurfaceSnapshotChunk>,
): void {
  const chunkedAtomCount = chunks.reduce((total, chunk) => total + (chunk.kind === 'atoms' ? chunk.atoms.length : 0), 0)
  const chunkedTargetCount = chunks.reduce((total, chunk) => total + (chunk.kind === 'targets' ? chunk.targets.length : 0), 0)
  const isComplete = chunkedAtomCount === atoms.length && chunkedTargetCount === targets.length
  if (!isComplete) {
    reportLog('[OfferPageSnapshot] atom/target count did not match total chunked count', {
      totalAtoms: atoms.length,
      chunkedAtoms: chunkedAtomCount,
      totalTargets: targets.length,
      chunkedTargets: chunkedTargetCount,
    })
  }

  invariant(isComplete, '[OfferPageSnapshot] atom/target count did not match total chunked count')
}

export function buildOfferPageSnapshot(input: OfferPageSnapshotInput): OfferPageSnapshotBuildResult | undefined {
  const surfaceContextScopeKey = buildOfferPageSnapshotScopeKeyFromSource(input.source)
  if (!surfaceContextScopeKey) {
    return undefined
  }

  const ageThresholds = getAssistantOccupancyAgeThresholdsFromSnapshotSource(input.source)
  const booking = selectionSnapshot(
    input.source.selection,
    ageThresholds,
    input.source.priceFormatContext,
    input.source.variant === 'flash' ? input.source.charterBundleRoute : undefined,
    input.source.variant === 'flash' ? input.source.charterItinerary : undefined,
    input.source.variant === 'flash' ? input.source.charterSelectableFareClasses : undefined,
    input.source.variant === 'flash' ? input.source.charterCheapestAvailableReward : undefined,
  )
  const overview = overviewSnapshot(input.source)
  const staticContent = staticContentSnapshot(input.source)
  const packages = packageSnapshot(input.source)
  const policies = policySnapshot(input.source)
  const reviewsSummary = reviewsSummarySnapshot(input.source)
  const contributionAtoms = normaliseContributionAtoms(input.contributions)
  const contributionCommitted = normaliseContributionCommittedState(input.contributions)
  // Contributions (unlike the fixed builder sections above) come from independent,
  // uncoordinated call sites — e.g. a review visible in both the inline section and
  // an open drawer would otherwise emit the same atomId twice. The ADR requires
  // duplicate atom IDs be rejected; keep the first deterministic one instead of
  // letting the backend reject the whole snapshot with duplicate_atom_id.
  const atoms = sortAtoms(uniqueBy([
    ...booking.atoms,
    ...overview.atoms,
    ...staticContent.atoms,
    ...packages.atoms,
    ...policies.atoms,
    ...reviewsSummary.atoms,
    ...contributionAtoms,
  ], atom => atom.atomId))
  const targets = addMissingAtomTargets(atoms, [
    ...booking.targets,
    ...overview.targets,
    ...staticContent.targets,
    ...packages.targets,
    ...policies.targets,
    ...reviewsSummary.targets,
  ])
  const recentCommittedState = sortCommittedState([...booking.committed, ...contributionCommitted])
  const manifestFacts = sortManifestFacts([...booking.facts, ...overview.facts, ...packageAvailabilityFacts(input.source)])
  const visibleState = input.contributions.reduce<{
    activeControl?: string
    activeDrawer?: string
    activeModal?: string
    suppressAutoFocus: boolean
  }>((state, contribution) => ({
    activeControl: contribution.visibleState?.activeControl ?? state.activeControl,
    activeDrawer: contribution.visibleState?.activeDrawer ?? state.activeDrawer,
    activeModal: contribution.visibleState?.activeModal ?? state.activeModal,
    suppressAutoFocus: state.suppressAutoFocus || contribution.visibleState?.suppressAutoFocus === true,
  }), {
    suppressAutoFocus: false,
  })
  const chunks = [
    buildCommittedStateChunk(recentCommittedState),
    buildAtomChunk('booking', atoms.filter(atom => atom.sourcePath?.startsWith('offer.booking.'))),
    buildTargetChunk('booking', targets.filter(target => target.sourcePath?.startsWith('offer.booking.'))),
    buildAtomChunk('packages', atoms.filter(atom => atom.sourcePath?.startsWith('offer.packages.'))),
    buildTargetChunk('packages', targets.filter(target => target.sourcePath?.startsWith('offer.packages.'))),
    buildAtomChunk('policies', atoms.filter(atom => belongsToPoliciesChunk(atom.sourcePath))),
    buildTargetChunk('policies', targets.filter(target => belongsToPoliciesChunk(target.sourcePath))),
    buildAtomChunk('property', atoms.filter(atom => belongsToPropertyChunk(atom.sourcePath))),
    buildTargetChunk('property', targets.filter(target => belongsToPropertyChunk(target.sourcePath))),
    buildAtomChunk('reviews', atoms.filter(atom => atom.sourcePath?.startsWith('offer.reviews.'))),
    buildTargetChunk('reviews', targets.filter(target => target.sourcePath?.startsWith('offer.reviews.'))),
    buildCapabilityChunk(),
  ]
  assertChunkCoverageComplete(atoms, targets, chunks)
  // Protects the customer's actual rate pick(s) from svc-trip's model-visible projection budget --
  // without this, a rate target with no other ranking signal can be truncated out of context purely
  // by array position on a property with many rooms/rates, even though its `:selected` atom is real,
  // correct content. A list (not the singular `selectedTargetId`) since a multi-room booking can
  // have one selected rate per room simultaneously.
  const manifest: AssistantSurfaceSnapshotChunkManifest = {
    surface: 'accommodation_offer_page',
    schemaVersion: 1,
    surfaceContextScopeKey,
    ...(input.source.pageUrl ? { pageUrl: input.source.pageUrl } : {}),
    visibleState: {
      ...visibleState,
      ...(packages.selectedTargetIds.length ? { visibleTargetIds: packages.selectedTargetIds } : {}),
      manifestFacts,
    },
    chunks: chunks.map(buildAssistantSurfaceSnapshotChunkRef),
  }
  const snapshot: AssistantSurfaceSnapshotRequest = {
    surface: 'accommodation_offer_page',
    schemaVersion: 1,
    surfaceContextScopeKey,
    ...(input.source.pageUrl ? { pageUrl: input.source.pageUrl } : {}),
    visibleState: manifest.visibleState,
    recentCommittedState,
    atoms,
    targets,
    capabilities,
  }

  return { chunks, manifest, snapshot }
}
